import { config } from './config.js';
import { CAPTURE_PATHS } from './capture-paths.mjs';
import { syncPhotoMemory, photoMemoryText, organizationSucceeded, photoJobPending, photoJobLabel } from './photo-memory.mjs';
import { apiClient } from './apiClient.js';
import { initSentry, captureError, setUser as setSentryUser } from './sentry.js';
import { initAuth, getUser, signIn, signOut, onAuthChange, isConfigured as isAuthConfigured } from './auth.js';
import { marked } from 'marked';
import DOMPurify from 'dompurify';

// Render AI/user-derived Markdown to SANITIZED HTML. marked emits raw HTML
// (including any <img onerror=…> present in the source), so every
// marked → innerHTML path MUST go through DOMPurify. The untrusted sources
// are transcripts, reflections, ask answers, and photo-OCR text — all of
// which are model output derived from user-supplied content.
function renderMarkdown(md) {
  return DOMPurify.sanitize(marked.parse(md || ''));
}
import JSZip from 'jszip';
import { DEMO_CAPTURES } from './demoData.js';
import { startTour, isTourPending } from './onboarding.js';
import { seedLibrary, clearAllCaptures } from './seedLibrary.js';

const DEMO_SEEDED_KEY = 'sayermore.demoSeeded';
const LIBRARY_PROMPT_SHOWN_KEY = 'sayermore.libraryPromptShown';

const DB_NAME = config.dbName;
const DB_VERSION = config.dbVersion;
const STORE = config.storeName;
const FREE_STORAGE_BYTES = config.freeStorageBytes;

const prompts = {
  person: [
    'Tell me about someone important to you.',
    'What is one detail about them that only close people would know?',
    'Is there a story that captures who they really are?',
    'What did they teach you by how they lived?',
  ],
  place: [
    "Tell me about a place that's shaped you.",
    'What does it sound like, smell like, or feel like there?',
    'Who else belongs in that place when you picture it?',
    'What would a stranger miss if you took them there?',
  ],
  skill: [
    "What's something you know how to do that's hard to explain?",
    'What do beginners usually get wrong?',
    'How do you know when you are doing it well?',
    'Who taught you, or how did you figure it out?',
  ],
  freeform: [
    "I'm listening. Start wherever feels natural.",
    'Say more about that.',
    'What happened next?',
    'What does that remind you of?',
  ],
};

const state = {
  db: null,
  captures: [],
  filter: 'all',
  mode: null,
  topic: 'freeform',
  promptIndex: 0,
  recorder: null,
  stream: null,
  chunks: [],
  audioBlob: null,
  photoImages: [],
  capturePath: null,
  audioContext: null,
  analyser: null,
  meterId: null,
  recognition: null,
  planStatus: 'trial_active',
  capabilities: {},
  activeTab: 'capture',
  libTab: 'organized',
  libTabAutoSet: false,
  folderView: 'root',
  sessionsView: 'list',
  folders: [],
  currentFolder: null,
  currentDocs: [],
  currentDoc: null,
  currentCapture: null,
  // null until first load; array (possibly empty) once fetched.
  reflections: null,
  // Per-device Ask history persisted to localStorage; loaded in init.
  askHistory: [],
  // Active conversation in the Ask sub-tab; null = empty state.
  currentAsk: null,
  // Sub-tab within #askPanel: 'ask' (current Q&A) or 'history' (past asks).
  askSubTab: 'ask',
  settings: {
    displayName: '',
    browserSpeech: true,
    // Confirm before destructive "Discard session" tap. Default ON; the
    // modal offers a "don't ask again" checkbox that flips this OFF, and
    // a toggle in Settings can flip it back on.
    confirmDiscard: true,
    // Read guided-session prompts aloud via /v1/tts. Default OFF —
    // some users hate TTS, some love it; let them opt in.
    speakPrompts: false,
    // Sync captures only when on WiFi. Default OFF. We ask once on
    // first launch via a banner; either choice (or "don't ask again")
    // persists here. Tri-state: undefined = haven't asked, false = ok
    // on any network, true = wifi only.
    syncWifiOnly: false,
    wifiPromptAnswered: false,
  },
};

const FOLDER_TONES = ['purple', 'amber', 'green', 'blue', 'rose', 'gold'];

const $ = (id) => document.getElementById(id);

window.addEventListener('DOMContentLoaded', init);

async function init() {
  await initSentry();
  // Public share viewer: /s/<token> renders a read-only shared note.
  // Bails out before auth/DB/SW/demo wiring — viewers need none of it
  // (and shouldn't pay the Clerk SDK load).
  const shareMatch = location.pathname.match(/^\/s\/([A-Za-z0-9_-]{16,64})$/);
  if (shareMatch) return initShareView(shareMatch[1]);
  buildWave();
  bindUi();
  state.settings = { ...state.settings, ...loadSettings() };
  state.askHistory = loadAskHistory();
  applyPlanState();
  applySettings();
  try {
    state.db = await openDb();
    await loadCaptures();
  } catch (e) {
    captureError(e, { stage: 'openDb' });
    toast('Local storage unavailable — captures will not persist.');
  }
  registerServiceWorker();
  setupSpeechRecognition();
  bindNetworkAutoResume();
  initBugErrorRing();
  setupStandaloneBackNav();
  const initialParams = new URLSearchParams(location.search);
  const initialTab = initialParams.get('tab') || (initialParams.has('capture') ? 'capture' : 'capture');
  applyTabUi(initialTab === 'library' ? 'library' : 'capture');
  // The Capture tab lands on the Audio | Photo chooser. Picking Audio starts
  // a fresh free-form session; picking Photo opens the photo screen.
  showCaptureChooser();
  selectCapturePath(initialParams.get('path'));
  window.addEventListener('popstate', (event) => {
    const nav = event.state?.sayermoreNav;
    if (nav) {
      applyNav(nav).catch((e) => captureError(e, { stage: 'popstate:applyNav' }));
    } else {
      // No nav state on this entry — treat as a fresh top-level navigation
      applyNav({ tab: 'capture' });
    }
  });
  await bootAuth();
  // Demo seed: if anonymous and the library is empty, seed sample captures
  // so the Sessions tab and "syncPendingCaptures" skip-list have something
  // to show. Folders/docs come from the apiClient demo stubs.
  await maybeSeedDemo();
  await loadCloudSessions();
  await loadFolders();
  renderLibrary();
  recordNav();
  syncPendingCaptures().catch((e) => captureError(e, { stage: 'syncPendingCaptures:init' }));
  // Tour runs only for anonymous visitors who haven't seen it. Deferred so
  // the rest of the UI is fully laid out before we measure spotlight rects.
  if (!isSignedIn() && isTourPending()) {
    setTimeout(() => startTour({ switchTab, setLibTab }), 250);
  }
}

async function maybeSeedDemo() {
  if (isSignedIn()) return;
  if (typeof localStorage === 'undefined') return;
  if (localStorage.getItem(DEMO_SEEDED_KEY) === '1') return;
  // localStorage was cleared but IDB still has demo records: restore the
  // flag so apiClient knows to return demo folders/docs. Otherwise the
  // user sees their seeded captures with no folders.
  if (state.captures.some((c) => c.demo === true)) {
    localStorage.setItem(DEMO_SEEDED_KEY, '1');
    return;
  }
  if (state.captures.length > 0) return; // user already has real data — don't pollute
  try {
    for (const cap of DEMO_CAPTURES) await putCapture(cap);
    await loadCaptures();
    localStorage.setItem(DEMO_SEEDED_KEY, '1');
  } catch (e) {
    captureError(e, { stage: 'maybeSeedDemo' });
  }
}

async function clearDemoData() {
  try {
    const demoIds = state.captures.filter((c) => c.demo === true).map((c) => c.id);
    for (const id of demoIds) {
      try { await deleteFromDb(id); } catch {}
    }
    if (demoIds.length) await loadCaptures();
  } catch (e) {
    captureError(e, { stage: 'clearDemoData' });
  }
  try { localStorage.removeItem(DEMO_SEEDED_KEY); } catch {}
}

// One-time first-sign-in offer: which demo-period captures should follow
// the user into their real library?
function showKeepCapturesModal(captures) {
  const modal = $('keepCapturesModal');
  const list = $('keepList');
  if (!modal || !list) return;
  list.innerHTML = captures.map((c) => {
    const preview = (c.text || '').slice(0, 140);
    return `<label class="keep-row">
      <input type="checkbox" data-capture-id="${escapeAttr(c.id)}" checked>
      <span class="keep-row-text">
        <span class="keep-row-title">${escapeHtml(c.title || 'Untitled capture')}</span>
        <span class="keep-row-preview">${escapeHtml(preview)}</span>
      </span>
    </label>`;
  }).join('');
  modal.classList.add('open');
  modal.setAttribute('aria-hidden', 'false');
}

function closeKeepCapturesModal() {
  const modal = $('keepCapturesModal');
  if (!modal) return;
  modal.classList.remove('open');
  modal.setAttribute('aria-hidden', 'true');
}

async function onKeepSelected() {
  const checked = new Set(Array.from(document.querySelectorAll('#keepList input[type=checkbox]:checked')).map((el) => el.dataset.captureId));
  const all = Array.from(document.querySelectorAll('#keepList input[type=checkbox]')).map((el) => el.dataset.captureId);
  // Delete the unchecked ones locally.
  for (const id of all) {
    if (!checked.has(id)) {
      try { await deleteFromDb(id); } catch (e) { captureError(e, { stage: 'onKeepSelected:delete', id }); }
    }
  }
  await loadCaptures();
  closeKeepCapturesModal();
  renderLibrary();
  // Sync the kept ones to cloud. syncPendingCaptures walks all
  // !cloudSynced captures, so the deleted-locally ones are naturally
  // excluded.
  syncPendingCaptures().catch((e) => captureError(e, { stage: 'onKeepSelected:sync' }));
}

async function onKeepDiscard() {
  const all = Array.from(document.querySelectorAll('#keepList input[type=checkbox]')).map((el) => el.dataset.captureId);
  for (const id of all) {
    try { await deleteFromDb(id); } catch (e) { captureError(e, { stage: 'onKeepDiscard:delete', id }); }
  }
  await loadCaptures();
  closeKeepCapturesModal();
  renderLibrary();
}

// First-sign-in starter modal: ask the user whether to seed a sample
// library. Triggers once per browser/account; respected after that.
function maybeShowLibraryStarter() {
  if (!isSignedIn()) return;
  if (typeof localStorage === 'undefined') return;
  if (localStorage.getItem(LIBRARY_PROMPT_SHOWN_KEY) === '1') return;
  if (state.folders.length > 0) {
    // User already has folders — don't pester them. Just record that we
    // would have shown it so future renders skip the check.
    try { localStorage.setItem(LIBRARY_PROMPT_SHOWN_KEY, '1'); } catch {}
    return;
  }
  // If the keep-captures modal is currently open, defer — both modals
  // shouldn't stack. The keep-captures buttons themselves don't re-trigger
  // this, so we rely on the user reloading after dismissing it. In
  // practice these two cases (demo captures pending + empty cloud
  // library) overlap rarely.
  const keepOpen = document.getElementById('keepCapturesModal')?.classList.contains('open');
  if (keepOpen) return;
  const modal = $('libraryStarterModal');
  if (!modal) return;
  modal.classList.add('open');
  modal.setAttribute('aria-hidden', 'false');
}

function closeLibraryStarter() {
  const modal = $('libraryStarterModal');
  if (!modal) return;
  modal.classList.remove('open');
  modal.setAttribute('aria-hidden', 'true');
  try { localStorage.setItem(LIBRARY_PROMPT_SHOWN_KEY, '1'); } catch {}
}

async function onStarterBlank() {
  closeLibraryStarter();
  toast('Starting blank. Record your first capture anytime.');
}

async function onStarterSample() {
  closeLibraryStarter();
  await runSeedLibrary();
}

async function runSeedLibrary() {
  toast('Setting up your library…');
  try {
    const result = await seedLibrary({
      onProgress: ({ stage, folders, docs, total }) => {
        if (stage === 'folders' && folders === total) toast(`Created ${folders} folders. Adding docs…`);
      },
    });
    await loadFolders();
    renderLibrary();
    if (result.failed > 0) {
      toast(`Library ready — ${result.failed} item(s) failed. Check logs.`);
    } else {
      toast(`Library ready: ${result.folders} folders, ${result.docs} docs.`);
    }
  } catch (e) {
    captureError(e, { stage: 'runSeedLibrary' });
    toast('Library setup failed. Try again from Settings.');
  }
}

// ---------- Bug reporting (Piece 6) ----------
// Tiny ring buffer of recent console errors/warnings. Attached as
// Installed PWAs (added-to-home-screen on iOS, installed on desktop) have no
// browser chrome — no system back button. Reveal an in-app back chevron in
// the topbar so cross-context navigation (e.g. session → doc) is reversible.
// In normal browser mode the system back button is already available, so the
// chevron stays hidden.
function setupStandaloneBackNav() {
  if (typeof window === 'undefined') return;
  const isStandalone =
    (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
    window.navigator?.standalone === true;
  if (!isStandalone) return;
  const btn = document.getElementById('navBackBtn');
  if (!btn) return;
  btn.hidden = false;
  btn.addEventListener('click', () => {
    // history.back() is a no-op if there's no entry. Most users will be deep
    // enough in nav (capture detail, doc detail, settings) for this to land.
    window.history.back();
  });
}

// metadata to bug reports so we can triage with context.
const bugErrorRing = [];
const BUG_RING_MAX = 20;
function initBugErrorRing() {
  if (typeof window === 'undefined') return;
  const orig = { error: console.error, warn: console.warn };
  ['error', 'warn'].forEach((level) => {
    console[level] = (...args) => {
      try {
        bugErrorRing.push({ level, t: Date.now(), msg: args.map((a) => {
          try { return typeof a === 'string' ? a : JSON.stringify(a); } catch { return String(a); }
        }).join(' ').slice(0, 500) });
        if (bugErrorRing.length > BUG_RING_MAX) bugErrorRing.shift();
      } catch {}
      try { orig[level].apply(console, args); } catch {}
    };
  });
  window.addEventListener('error', (e) => {
    bugErrorRing.push({ level: 'error', t: Date.now(), msg: `[uncaught] ${e?.message || ''}`.slice(0, 500) });
    if (bugErrorRing.length > BUG_RING_MAX) bugErrorRing.shift();
  });
}

let bugRecorder = null;
let bugStream = null;
let bugChunks = [];
let bugBlob = null;

function onReportBugClick() {
  if (!apiClient.enabled) {
    toast('Sign in to report a bug. (Anonymous sessions can\'t be triaged.)');
    return;
  }
  closeSettings();
  bugBlob = null;
  bugChunks = [];
  $('bugTranscript').value = '';
  $('bugStatusText').textContent = 'Tap to record';
  $('bugRecordBtn').classList.remove('recording');
  const modal = $('bugReportModal');
  if (modal) { modal.classList.add('open'); modal.setAttribute('aria-hidden', 'false'); }
}

function closeBugModal() {
  if (bugRecorder && bugRecorder.state === 'recording') {
    try { bugRecorder.stop(); } catch {}
  }
  if (bugStream) { bugStream.getTracks().forEach((t) => t.stop()); bugStream = null; }
  const modal = $('bugReportModal');
  if (modal) { modal.classList.remove('open'); modal.setAttribute('aria-hidden', 'true'); }
}

async function toggleBugRecording() {
  if (bugRecorder && bugRecorder.state === 'recording') {
    try { bugRecorder.stop(); } catch {}
    return;
  }
  try {
    bugStream = await navigator.mediaDevices.getUserMedia({ audio: true });
    bugChunks = [];
    bugRecorder = new MediaRecorder(bugStream);
    bugRecorder.addEventListener('dataavailable', (e) => { if (e.data?.size) bugChunks.push(e.data); });
    bugRecorder.addEventListener('stop', async () => {
      bugBlob = new Blob(bugChunks, { type: bugRecorder.mimeType || 'audio/webm' });
      $('bugRecordBtn').classList.remove('recording');
      $('bugStatusText').textContent = 'Transcribing…';
      try {
        const ext = audioExtFromType(bugBlob.type);
        const result = await apiClient.transcribeAudio(bugBlob, `bug-${Date.now()}.${ext}`);
        const text = String(result?.text || '').trim();
        if (text) {
          $('bugTranscript').value = $('bugTranscript').value
            ? `${$('bugTranscript').value}\n${text}`
            : text;
          $('bugStatusText').textContent = 'Recorded. Edit if needed, then Submit.';
        } else {
          $('bugStatusText').textContent = 'Audio captured. Type or re-record.';
        }
      } catch (e) {
        captureError(e, { stage: 'toggleBugRecording:transcribe' });
        $('bugStatusText').textContent = 'Transcription failed. Type the bug below.';
      }
      if (bugStream) { bugStream.getTracks().forEach((t) => t.stop()); bugStream = null; }
    });
    bugRecorder.start();
    $('bugRecordBtn').classList.add('recording');
    $('bugStatusText').textContent = 'Recording… tap mic to stop.';
  } catch (e) {
    captureError(e, { stage: 'toggleBugRecording:start' });
    $('bugStatusText').textContent = 'Couldn\'t access the mic. Type the bug below.';
  }
}

async function onBugSubmit() {
  const transcript = $('bugTranscript').value.trim();
  if (!transcript) {
    toast('Record or type the bug first.');
    return;
  }
  const metadata = {
    userAgent: navigator.userAgent,
    clientVersion: config.clientVersion,
    apiVersion: config.apiVersion,
    activeTab: state.activeTab,
    libTab: state.libTab,
    captureCount: state.captures?.length || 0,
    folderCount: state.folders?.length || 0,
    connectionType: detectConnectionType(),
    recentErrors: bugErrorRing.slice(),
    submittedAt: new Date().toISOString(),
  };
  const btn = $('bugSubmitBtn');
  btn.disabled = true; btn.classList.add('busy');
  try {
    const result = await apiClient.submitBugReport({ transcript, metadata });
    if (result?.ok) {
      toast('Bug report sent. Thank you.');
      closeBugModal();
    } else {
      toast('Submit failed. Try again.');
    }
  } finally {
    btn.disabled = false; btn.classList.remove('busy');
  }
}

// Piece 4: Client-side dedupe of identical captures. Normalize text
// (lowercase + collapse whitespace), group, keep the earliest by
// createdAt, delete the rest locally + via DELETE /v1/sessions/:id.
async function onDedupeClick() {
  const normalize = (t) => (t || '').toLowerCase().replace(/\s+/g, ' ').trim();
  const groups = new Map();
  for (const c of state.captures) {
    if (c.demo === true) continue;
    const key = normalize(c.text);
    if (!key) continue;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(c);
  }
  const toDelete = [];
  for (const list of groups.values()) {
    if (list.length < 2) continue;
    list.sort((a, b) => (a.createdAt || '').localeCompare(b.createdAt || ''));
    for (let i = 1; i < list.length; i += 1) toDelete.push(list[i]);
  }
  if (toDelete.length === 0) {
    toast('No duplicates found.');
    return;
  }
  const ok = confirm(`Delete ${toDelete.length} duplicate capture${toDelete.length === 1 ? '' : 's'}? This is permanent.`);
  if (!ok) return;
  closeSettings();
  toast(`Removing ${toDelete.length} duplicate${toDelete.length === 1 ? '' : 's'}…`);
  for (const cap of toDelete) {
    try { await deleteFromDb(cap.id); } catch (e) { captureError(e, { stage: 'onDedupeClick:deleteFromDb', id: cap.id }); }
    if (apiClient.enabled) {
      apiClient.deleteSession(cap.id).catch((e) => captureError(e, { stage: 'onDedupeClick:deleteSession', id: cap.id }));
    }
  }
  await loadCaptures();
  renderLibrary();
  toast(`Removed ${toDelete.length} duplicate${toDelete.length === 1 ? '' : 's'}.`);
}

// Trash modal: lists folders / docs / sessions currently soft-deleted, with
// Restore actions per item and an "Empty trash" hard-delete at the bottom.
// The 30-day boot sweep on the server also hard-deletes; this is just the
// manual escape hatch.
async function openTrashModal() {
  const modal = document.getElementById('trashModal');
  if (!modal) return;
  modal.classList.add('open');
  modal.setAttribute('aria-hidden', 'false');
  await renderTrashList();
}

function closeTrashModal() {
  const modal = document.getElementById('trashModal');
  if (!modal) return;
  modal.classList.remove('open');
  modal.setAttribute('aria-hidden', 'true');
}

async function renderTrashList() {
  const host = document.getElementById('trashList');
  if (!host) return;
  if (!apiClient.enabled) {
    host.innerHTML = '<p style="color:var(--muted);font-size:13px;">Sign in to see your trash.</p>';
    return;
  }
  host.innerHTML = '<p style="color:var(--muted);font-size:13px;">Loading…</p>';
  const trash = await apiClient.getTrash();
  const rows = [];
  for (const f of trash.folders || []) {
    rows.push({ kind: 'folder', key: f.key, label: f.title || f.key, trashedAt: f.trashedAt });
  }
  for (const d of trash.docs || []) {
    rows.push({ kind: 'doc', folderKey: d.folderKey, key: d.key, label: d.title || d.key, trashedAt: d.trashedAt });
  }
  for (const s of trash.sessions || []) {
    const preview = (s.transcriptPreview || '').slice(0, 60) || 'Capture';
    rows.push({ kind: 'session', id: s.id, label: preview, trashedAt: s.trashedAt });
  }
  if (rows.length === 0) {
    host.innerHTML = '<p style="color:var(--muted);font-size:13px;">Trash is empty.</p>';
    return;
  }
  host.innerHTML = rows.map((row, i) => {
    const ago = formatDate(row.trashedAt);
    const kindLabel = row.kind === 'folder' ? 'Folder' : row.kind === 'doc' ? 'Note' : 'Capture';
    return `<div class="trash-row" data-i="${i}" style="display:flex;align-items:center;gap:10px;padding:10px;border:1px solid var(--line);border-radius:10px;background:var(--card);">
      <div style="flex:1;min-width:0;overflow:hidden;">
        <div style="font-size:13px;font-weight:600;color:var(--ink);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escapeHtml(row.label)}</div>
        <div style="font-size:11px;color:var(--muted);">${kindLabel} · trashed ${escapeHtml(ago)}</div>
      </div>
      <button class="pill" data-restore="${i}" style="font-size:11px;padding:6px 10px;"><i class="fa-solid fa-rotate-left"></i> Restore</button>
    </div>`;
  }).join('');
  host.querySelectorAll('[data-restore]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const i = Number(btn.getAttribute('data-restore'));
      const row = rows[i];
      const payload = row.kind === 'folder'
        ? { kind: 'folder', key: row.key }
        : row.kind === 'doc'
          ? { kind: 'doc', folderKey: row.folderKey, key: row.key }
          : { kind: 'session', id: row.id };
      btn.disabled = true;
      try {
        await apiClient.restoreTrash(payload);
        toast('Restored');
        await renderTrashList();
        await loadFolders();
        await loadCloudSessions().catch(() => {});
        renderLibrary();
      } catch (e) {
        captureError(e, { stage: 'restoreTrash' });
        toast('Restore failed.');
        btn.disabled = false;
      }
    });
  });
}

async function onEmptyTrashClick() {
  if (!confirm('Permanently delete everything in trash? This cannot be undone.')) return;
  if (!apiClient.enabled) {
    toast('Sign in to manage trash.');
    return;
  }
  try {
    await apiClient.emptyTrash();
    toast('Trash emptied');
    await renderTrashList();
  } catch (e) {
    captureError(e, { stage: 'emptyTrash' });
    toast('Empty trash failed.');
  }
}

async function onResetAccount() {
  const ok = confirm(
    'This deletes all your captures and replaces your library with the sample library. Real captures cannot be recovered. Continue?',
  );
  if (!ok) return;
  closeSettings();
  toast('Clearing captures…');
  try {
    const cleared = await clearAllCaptures({ state, deleteFromDb, loadCaptures });
    toast(`Cleared ${cleared} capture${cleared === 1 ? '' : 's'}. Installing sample library…`);
  } catch (e) {
    captureError(e, { stage: 'onResetAccount:clear' });
    toast('Failed to clear captures.');
    return;
  }
  await runSeedLibrary();
}

async function bootAuth() {
  // Always paint the auth UI from local state first. If Clerk's SDK is slow
  // or fails entirely (CDN flake, SW cache, OAuth handoff weirdness), the
  // user still sees the sign-in pill instead of a hidden #authBtn — they
  // previously had no path back to signing in if initAuth threw.
  applyAuthState();
  if (!isAuthConfigured()) return;
  try {
    // 8s timeout race so a hanging clerk.load() can't strand the UI in
    // "loading…" forever. Generous for mobile cellular; short enough that
    // a real network failure surfaces fast.
    await Promise.race([
      initAuth(),
      new Promise((_, reject) => setTimeout(() => reject(new Error('auth_timeout')), 8000)),
    ]);
    let lastSignedIn = Boolean(getUser());
    // Recompute plan state now that Clerk has loaded — the initial call in
    // init() ran before getUser() was available, so the lifetime allowlist
    // (and any future per-user plan logic) couldn't apply on cold boot.
    applyPlanState();
    onAuthChange(async () => {
      applyAuthState();
      // Re-apply plan state on every auth transition so signing out drops
      // the lifetime grant + signing in restores it.
      applyPlanState();
      const nowSignedIn = Boolean(getUser());
      const justSignedIn = nowSignedIn && !lastSignedIn;
      lastSignedIn = nowSignedIn;
      try {
        if (justSignedIn) {
          // Demo handoff: clear seeded sample data, then offer to keep any
          // captures the visitor made themselves during the demo.
          const pendingUserCaptures = state.captures.filter((c) => c.demo !== true && c.cloudSynced !== true && (c.text || '').trim());
          await clearDemoData();
          if (pendingUserCaptures.length > 0) {
            showKeepCapturesModal(pendingUserCaptures);
          }
        }
        await loadCloudSessions();
        await loadFolders();
        renderLibrary();
        if (!justSignedIn) {
          // Auto-sync only when nothing is up for review. If we just signed
          // in with pending demo captures, the modal handles the sync.
          await syncPendingCaptures();
        }
        if (justSignedIn) {
          // After cloud fetch + render: if this is a fresh account (no
          // folders yet) and we haven't shown the starter prompt before,
          // offer the sample library. Keep-captures modal (if it fired)
          // already takes priority — starter only opens once that's
          // dismissed or skipped, which the user does by clicking either
          // button in it.
          maybeShowLibraryStarter();
        }
      } catch (e) {
        captureError(e, { stage: 'onAuthChange' });
      }
    });
    applyAuthState();
  } catch (e) {
    captureError(e, { stage: 'initAuth' });
    // Re-paint so the signed-out pill is visible even when Clerk failed
    // to load — without this the user is stranded with #authBtn:none.
    applyAuthState();
  }
}

function applyAuthState() {
  const user = getUser();
  setSentryUser(user);
  // Founder-only Admin tab. Cosmetic gate — the API 404s /v1/admin for
  // everyone else regardless of what the client shows.
  const adminBtn = document.getElementById('adminTabBtn');
  const admin = isAdminUser();
  if (adminBtn) adminBtn.style.display = admin ? '' : 'none';
  if (!admin && state.activeTab === 'admin') switchTab('capture');
  const btn = document.getElementById('authBtn');
  const prompt = document.getElementById('signinPrompt');
  const configured = isAuthConfigured();

  // Top-right auth control. Signed-in users see a tidy avatar circle.
  // Signed-out users see a labeled "Sign in" pill — icons alone (the old
  // fa-right-to-bracket) were unreadable to people who'd never seen the
  // glyph before. The button morphs between two modes with explicit
  // class changes so CSS can size each correctly.
  if (btn) {
    if (!configured) {
      btn.style.display = 'none';
    } else if (user) {
      btn.style.display = 'inline-flex';
      const email = user.primaryEmailAddress?.emailAddress || '';
      const initial = (user.firstName?.[0] || email[0] || '?').toUpperCase();
      btn.innerHTML = `<span class="auth-avatar">${initial}</span>`;
      btn.classList.add('auth-signed-in');
      btn.classList.remove('auth-pill');
      const signedInLabel = `Sign out (${email || 'signed in'})`;
      btn.title = signedInLabel;
      btn.setAttribute('aria-label', signedInLabel);
      btn.onclick = () => {
        if (confirm(`Sign out of ${email || 'Sayermore'}?`)) signOut();
      };
    } else {
      btn.style.display = 'inline-flex';
      btn.innerHTML = '<i class="fa-solid fa-right-to-bracket"></i><span class="auth-pill-text">Sign in</span>';
      btn.classList.remove('auth-signed-in');
      btn.classList.add('auth-pill');
      btn.title = 'Sign in';
      btn.setAttribute('aria-label', 'Sign in');
      btn.onclick = async () => {
        const ok = await signIn();
        if (!ok) toast('Sign-in unavailable. Check your connection and try again.');
      };
    }
  }

  // Soft-gate banner (only when auth is configured, user is signed out, and not dismissed this session)
  if (prompt) {
    const dismissed = sessionStorage.getItem('sayermore.signinDismissed') === '1';
    prompt.style.display = configured && !user && !dismissed ? 'flex' : 'none';
  }
}

function isSignedIn() {
  return Boolean(getUser());
}

function selectCapturePath(key) {
  const path = Object.hasOwn(CAPTURE_PATHS, key) ? CAPTURE_PATHS[key] : null;
  state.capturePath = path ? key : null;
  document.querySelectorAll('[data-capture-path]').forEach((button) => {
    button.setAttribute('aria-pressed', String(button.dataset.capturePath === state.capturePath));
  });
  $('capturePathHint').textContent = path?.hint || 'A voice note, a photo, or both. Start wherever you like.';
  $('clearCapturePath').hidden = !path;
  $('chooseAudioBtn').querySelector('small').textContent = path?.audio || 'Open the mic and start talking.';
  $('choosePhotoBtn').querySelector('small').textContent = path?.photo || 'Save a photo with the story behind it.';
  // A placeholder is a suggestion, never a saved explanation. Existing drafts
  // and recordings remain untouched when someone changes their starting point.
  $('photoExplanation').placeholder = path?.placeholder || CAPTURE_PATHS.find.placeholder;
}

function bindUi() {
  document.querySelectorAll('[data-capture-path]').forEach((button) => button.addEventListener('click', () => {
    selectCapturePath(state.capturePath === button.dataset.capturePath ? null : button.dataset.capturePath);
  }));
  $('clearCapturePath').addEventListener('click', () => selectCapturePath(null));
  document.querySelectorAll('[data-ask-path]').forEach((button) => button.addEventListener('click', () => {
    const example = CAPTURE_PATHS[button.dataset.askPath];
    if (!example) return;
    $('askInput').value = example.question;
    autoGrowAskInput();
    $('askInput').focus();
  }));
  document.querySelectorAll('[data-tab]').forEach((btn) => btn.addEventListener('click', () => switchTab(btn.dataset.tab)));
  document.querySelectorAll('[data-mode]').forEach((btn) => btn.addEventListener('click', () => pickMode(btn.dataset.mode)));
  document.querySelectorAll('[data-topic]').forEach((btn) => btn.addEventListener('click', () => startSession('guided', btn.dataset.topic)));
  document.querySelectorAll('[data-back]').forEach((btn) => btn.addEventListener('click', () => showScreen(btn.dataset.back)));
  document.querySelectorAll('[data-libtab]').forEach((btn) => btn.addEventListener('click', () => setLibTab(btn.dataset.libtab)));
  $('crumbHome').addEventListener('click', (e) => { e.preventDefault(); closeFolder(); });
  $('micBtn').addEventListener('click', toggleRecording);
  $('nextPromptBtn').addEventListener('click', nextPrompt);
  $('saveSessionBtn').addEventListener('click', saveSession);
  $('cancelSessionBtn').addEventListener('click', onDiscardSessionTap);
  const chooseAudioBtn = $('chooseAudioBtn');
  if (chooseAudioBtn) chooseAudioBtn.addEventListener('click', () => startSession('freeform', 'freeform'));
  const choosePhotoBtn = $('choosePhotoBtn');
  if (choosePhotoBtn) choosePhotoBtn.addEventListener('click', showPhotoScreen);
  const photoCameraInput = $('photoCameraInput');
  if (photoCameraInput) photoCameraInput.addEventListener('change', (e) => onPhotoFilesAdded(e.target.files));
  const photoUploadInput = $('photoUploadInput');
  if (photoUploadInput) photoUploadInput.addEventListener('change', (e) => onPhotoFilesAdded(e.target.files));
  const photoSynthBtn = $('photoSynthBtn');
  if (photoSynthBtn) photoSynthBtn.addEventListener('click', savePhotoCapture);
  const photoDiscardBtn = $('photoDiscardBtn');
  if (photoDiscardBtn) photoDiscardBtn.addEventListener('click', showCaptureChooser);
  $('photoVoiceBtn').addEventListener('click', togglePhotoVoice);
  $('photoVoiceRemove').addEventListener('click', clearPhotoVoice);
  const storeImageToggle = $('storeImageToggle');
  if (storeImageToggle) storeImageToggle.addEventListener('change', onStoreImageToggle);
  $('discardCancelBtn').addEventListener('click', closeDiscardModal);
  $('discardConfirmBtn').addEventListener('click', onDiscardConfirm);
  $('settingsBtn').addEventListener('click', openSettings);
  $('closeSettings').addEventListener('click', closeSettings);
  $('saveSettings').addEventListener('click', saveSettings);
  const speakerBtn = $('speakerToggleBtn');
  if (speakerBtn) speakerBtn.addEventListener('click', onSpeakerToggle);
  const dedupeBtn = $('dedupeBtn');
  if (dedupeBtn) dedupeBtn.addEventListener('click', onDedupeClick);
  const trashBtn = $('trashBtn');
  if (trashBtn) trashBtn.addEventListener('click', openTrashModal);
  const trashCloseBtn = $('trashCloseBtn');
  if (trashCloseBtn) trashCloseBtn.addEventListener('click', closeTrashModal);
  const emptyTrashBtn = $('emptyTrashBtn');
  if (emptyTrashBtn) emptyTrashBtn.addEventListener('click', onEmptyTrashClick);
  const generateReflectionBtn = $('generateReflectionBtn');
  if (generateReflectionBtn) generateReflectionBtn.addEventListener('click', onGenerateReflectionClick);
  const syncNowBtn = $('syncNowBtn');
  if (syncNowBtn) syncNowBtn.addEventListener('click', onSyncNowClick);
  const pendingSyncPill = $('pendingSyncPill');
  if (pendingSyncPill) pendingSyncPill.addEventListener('click', onSyncNowClick);
  const reportBugBtn = $('reportBugBtn');
  if (reportBugBtn) reportBugBtn.addEventListener('click', onReportBugClick);
  const bugSubmitBtn = $('bugSubmitBtn');
  if (bugSubmitBtn) bugSubmitBtn.addEventListener('click', onBugSubmit);
  const bugCancelBtn = $('bugCancelBtn');
  if (bugCancelBtn) bugCancelBtn.addEventListener('click', closeBugModal);
  const bugRecordBtn = $('bugRecordBtn');
  if (bugRecordBtn) bugRecordBtn.addEventListener('click', toggleBugRecording);
  const tourBtn = $('showTourBtn');
  if (tourBtn) tourBtn.addEventListener('click', () => { closeSettings(); setTimeout(() => startTour({ switchTab, setLibTab }), 200); });
  const keepSelected = $('keepSelectedBtn');
  if (keepSelected) keepSelected.addEventListener('click', onKeepSelected);
  const keepDiscard = $('keepDiscardBtn');
  if (keepDiscard) keepDiscard.addEventListener('click', onKeepDiscard);
  const starterBlank = $('starterBlankBtn');
  if (starterBlank) starterBlank.addEventListener('click', onStarterBlank);
  const starterSample = $('starterSampleBtn');
  if (starterSample) starterSample.addEventListener('click', onStarterSample);
  const resetBtn = $('resetAccountBtn');
  if (resetBtn) resetBtn.addEventListener('click', onResetAccount);
  $('searchBtn').addEventListener('click', openSearch);
  $('searchOverlay').addEventListener('click', (e) => { if (e.target.id === 'searchOverlay') closeSearch(); });
  $('searchInput').addEventListener('input', runSearch);
  const askForm = document.getElementById('askForm');
  if (askForm) askForm.addEventListener('submit', onAskSubmit);
  const clearAskHistoryBtn = document.getElementById('clearAskHistoryBtn');
  if (clearAskHistoryBtn) clearAskHistoryBtn.addEventListener('click', onClearAskHistoryClick);
  document.querySelectorAll('#askSubtabs button[data-asksub]').forEach((btn) => {
    btn.addEventListener('click', () => setAskSubTab(btn.dataset.asksub));
  });
  const askInput = document.getElementById('askInput');
  if (askInput) {
    askInput.addEventListener('input', autoGrowAskInput);
    askInput.addEventListener('keydown', (e) => {
      // Enter submits; Shift+Enter inserts a newline. Native textarea behavior
      // otherwise treats Enter as a newline — override that for chat feel.
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        onAskSubmit();
      }
    });
  }
  $('captureCrumbHome').addEventListener('click', (e) => { e.preventDefault(); closeCapture(); });
  $('docCrumbHome').addEventListener('click', (e) => { e.preventDefault(); closeDoc(true); });
  $('docCrumbFolder').addEventListener('click', (e) => { e.preventDefault(); closeDoc(false); });
  const signinBtn = $('signinPromptBtn');
  if (signinBtn) signinBtn.addEventListener('click', async () => {
    const ok = await signIn();
    if (!ok) toast('Sign-in unavailable. Check your connection and try again.');
  });
  const dismissBtn = $('signinPromptDismiss');
  if (dismissBtn) dismissBtn.addEventListener('click', () => {
    sessionStorage.setItem('sayermore.signinDismissed', '1');
    $('signinPrompt').style.display = 'none';
  });
  const orgSignIn = $('organizedSignInBtn');
  if (orgSignIn) orgSignIn.addEventListener('click', async () => {
    const ok = await signIn();
    if (!ok) toast('Sign-in unavailable. Check your connection and try again.');
  });
  const sessionsSignIn = $('sessionsSignInBtn');
  if (sessionsSignIn) sessionsSignIn.addEventListener('click', async () => {
    const ok = await signIn();
    if (!ok) toast('Sign-in unavailable. Check your connection and try again.');
  });
}

function applyTabUi(tab) {
  state.activeTab = tab;
  document.querySelectorAll('.tabs button').forEach((btn) => btn.classList.toggle('active', btn.dataset.tab === tab));
  $('capturePanel').classList.toggle('active', tab === 'capture');
  $('libraryPanel').classList.toggle('active', tab === 'library');
  const askPanel = document.getElementById('askPanel');
  if (askPanel) askPanel.classList.toggle('active', tab === 'ask');
  const adminPanel = document.getElementById('adminPanel');
  if (adminPanel) adminPanel.classList.toggle('active', tab === 'admin');
  if (tab === 'admin') loadAdminOverview();
  document.querySelector('.library-only').style.display = tab === 'library' ? 'grid' : 'none';
  // Blank-on-entry for the Ask tab: clear the composer, drop the current
  // conversation, default to the Ask sub-tab. History sub-tab still shows
  // the persisted past asks.
  if (tab === 'ask') {
    const input = document.getElementById('askInput');
    if (input) { input.value = ''; }
    state.currentAsk = null;
    setAskSubTab('ask');
    autoGrowAskInput();
  }
}

function switchTab(tab) {
  if (photoRecorder?.state === 'recording') stopPhotoVoice();
  applyTabUi(tab);
  recordNav();
  // Belt-and-suspenders for the iOS Safari focus-after-tap bug: after
  // switching, drop focus from any active element so no :focus-visible
  // ring lingers around the bottom-nav button.
  try { document.activeElement && document.activeElement.blur(); } catch {}
}

let navSuppressed = false;

function currentNav() {
  return {
    tab: state.activeTab || 'capture',
    libTab: state.libTab || 'organized',
    folderView: state.folderView || 'root',
    sessionsView: state.sessionsView || 'list',
    folderKey: state.currentFolder?.key || null,
    docKey: state.currentDoc?.key || null,
    captureId: state.currentCapture?.id || null,
  };
}

function navsEqual(a, b) {
  if (!a || !b) return false;
  return a.tab === b.tab && a.libTab === b.libTab && a.folderView === b.folderView
    && a.sessionsView === b.sessionsView && a.folderKey === b.folderKey
    && a.docKey === b.docKey && a.captureId === b.captureId;
}

function recordNav() {
  if (navSuppressed) return;
  const nav = currentNav();
  const url = `${window.location.pathname}?tab=${nav.tab}`;
  const prev = history.state?.sayermoreNav;
  if (!prev) {
    history.replaceState({ sayermoreNav: nav }, '', url);
  } else if (!navsEqual(prev, nav)) {
    history.pushState({ sayermoreNav: nav }, '', url);
  }
}

async function applyNav(nav) {
  if (!nav) return;
  navSuppressed = true;
  try {
    applyTabUi(nav.tab || 'capture');
    if (nav.tab !== 'library') return;
    state.libTab = nav.libTab || 'organized';
    state.libTabAutoSet = true;
    if (nav.libTab === 'sessions') {
      state.folderView = 'root';
      state.currentFolder = null;
      state.currentDoc = null;
      if (nav.captureId) {
        const c = state.captures.find((x) => x.id === nav.captureId);
        if (c) {
          state.sessionsView = 'detail';
          state.currentCapture = c;
        } else {
          state.sessionsView = 'list';
          state.currentCapture = null;
        }
      } else {
        state.sessionsView = 'list';
        state.currentCapture = null;
      }
      renderLibrary();
      return;
    }
    // Organized branch
    state.sessionsView = 'list';
    state.currentCapture = null;
    if (nav.folderKey && nav.docKey) {
      await openFolder(nav.folderKey);
      await openDoc(nav.folderKey, nav.docKey);
    } else if (nav.folderKey) {
      await openFolder(nav.folderKey);
    } else {
      state.folderView = 'root';
      state.currentFolder = null;
      state.currentDoc = null;
      renderLibrary();
    }
  } finally {
    navSuppressed = false;
  }
}

function showScreen(id) {
  if (id !== 'photoScreen' && photoRecorder?.state === 'recording') stopPhotoVoice();
  document.querySelectorAll('.screen').forEach((screen) => screen.classList.toggle('active', screen.id === id));
}

function pickMode(mode) {
  if (!state.capabilities.canCaptureWeb) {
    toast('Your trial has ended. Export your captures anytime, or subscribe to keep using Sayermore Cloud.');
    switchTab('library');
    return;
  }
  state.mode = mode;
  if (mode === 'guided') showScreen('topicScreen');
  else startSession('freeform', 'freeform');
}

function startSession(mode, topic) {
  // Gate at the door: if the local library is already full, prompt to free
  // up space instead of letting them record something that can't be saved.
  if (isStorageFull()) { showStorageLimitModal(); return; }
  state.mode = mode;
  state.topic = topic;
  state.promptIndex = 0;
  state.audioBlob = null;
  state.chunks = [];
  $('noteText').value = '';
  const starter = mode === 'freeform' ? CAPTURE_PATHS[state.capturePath] : null;
  $('promptText').hidden = !starter;
  setPrompt(starter?.hint || prompts[topic][0]);
  setStatus('Tap to speak');
  showScreen('sessionScreen');
}

function nextPrompt() {
  const list = prompts[state.topic] || prompts.freeform;
  state.promptIndex = (state.promptIndex + 1) % list.length;
  setPrompt(list[state.promptIndex]);
}

function setPrompt(text) {
  $('promptText').textContent = text;
  maybeSpeakPrompt(text);
}

// Spoken prompts — opt-in via Settings → "Speak prompts aloud".
//
// Two tiers of audio:
//   1. Signed in → POST /v1/tts (OpenAI gpt-4o-mini-tts + sage voice).
//      Best quality; matches the desktop app's vibe.
//   2. Anonymous / API failure → window.speechSynthesis (browser TTS).
//      Lower quality but free, instant, and works for the demo flow
//      where the user hasn't signed in yet.
//
// Concurrency rules:
//   - Cache the last text we spoke; if setPrompt is called repeatedly
//     with the same text, only synthesize once.
//   - When the mic starts recording, stopPromptAudio() silences both
//     <audio> playback and browser TTS so the mic doesn't pick up the
//     speaker output.
//   - Failures are silent. Text is on screen either way.
let promptAudioEl = null;
let promptAudioToken = 0;
let lastSpokenText = '';

async function maybeSpeakPrompt(text) {
  if (!state.settings.speakPrompts) return;
  if (!text || !text.trim()) return;
  if (text === lastSpokenText && (isAudioElPlaying() || isBrowserSpeaking())) return;
  lastSpokenText = text;
  stopPromptAudio();

  // Tier 1: signed in → high-quality OpenAI TTS via the cloud route.
  if (apiClient.enabled) {
    const token = ++promptAudioToken;
    const blob = await apiClient.synthesizeSpeech(text);
    if (token !== promptAudioToken) return; // newer prompt cancelled this one
    if (blob) {
      try {
        const url = URL.createObjectURL(blob);
        const audio = new Audio(url);
        audio.addEventListener('ended', () => URL.revokeObjectURL(url));
        promptAudioEl = audio;
        try {
          await audio.play();
          return;
        } catch {
          // play() rejected (autoplay policy, etc.) — fall through to browser TTS
          promptAudioEl = null;
          URL.revokeObjectURL(url);
        }
      } catch (e) {
        captureError(e, { stage: 'maybeSpeakPrompt:openai-play' });
      }
    }
    // OpenAI path returned nothing or failed — fall through to browser TTS
    // so the user still hears something.
  }

  // Tier 2: browser-native speechSynthesis. Free, works without auth.
  speakViaBrowser(text);
}

function speakViaBrowser(text) {
  if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;
  try {
    window.speechSynthesis.cancel();
    const utter = new SpeechSynthesisUtterance(text);
    utter.rate = 0.95;
    utter.pitch = 1.0;
    utter.volume = 1.0;
    window.speechSynthesis.speak(utter);
  } catch (e) {
    captureError(e, { stage: 'speakViaBrowser' });
  }
}

function isAudioElPlaying() {
  return Boolean(promptAudioEl && !promptAudioEl.paused);
}
function isBrowserSpeaking() {
  return typeof window !== 'undefined' && 'speechSynthesis' in window && window.speechSynthesis.speaking;
}

function stopPromptAudio() {
  if (promptAudioEl) {
    try { promptAudioEl.pause(); promptAudioEl.currentTime = 0; } catch {}
    promptAudioEl = null;
  }
  // Cancel any in-flight browser TTS utterance too.
  if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
    try { window.speechSynthesis.cancel(); } catch {}
  }
  // Bump the token so any in-flight fetch's audio is discarded on arrival.
  promptAudioToken++;
}

// In-session speaker icon — quick toggle without opening Settings.
// Flips state.settings.speakPrompts, persists, and updates the icon
// visual. If turning OFF mid-playback, also stops the audio. If turning
// ON, speak the current prompt immediately so the user gets feedback.
function onSpeakerToggle() {
  const next = !state.settings.speakPrompts;
  state.settings.speakPrompts = next;
  try { localStorage.setItem('sayermore.settings', JSON.stringify(state.settings)); } catch {}
  const icon = $('speakerToggleBtn');
  if (icon) icon.classList.toggle('active', next);
  if (!next) {
    stopPromptAudio();
  } else {
    const current = $('promptText')?.textContent || '';
    // Reset cache so the same text speaks again.
    lastSpokenText = '';
    maybeSpeakPrompt(current);
  }
}

async function toggleRecording() {
  if (state.recorder && state.recorder.state === 'recording') {
    state.recorder.stop();
    return;
  }
  // Kill any in-flight prompt audio before the mic opens — otherwise the
  // user's recording would pick up the speaker output.
  stopPromptAudio();
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    state.stream = stream;
    state.chunks = [];
    state.recorder = new MediaRecorder(stream);
    state.recorder.addEventListener('dataavailable', (e) => {
      if (e.data && e.data.size) state.chunks.push(e.data);
    });
    state.recorder.addEventListener('stop', finishRecording);
    state.recorder.start();
    startMeter(stream);
    startSpeech();
    $('micBtn').classList.add('recording');
    setStatus('Recording', 'rec');
  } catch (e) {
    toast(e.name === 'NotAllowedError' ? 'Microphone permission was denied.' : 'Microphone unavailable.');
  }
}

function finishRecording() {
  state.audioBlob = new Blob(state.chunks, { type: state.recorder.mimeType || 'audio/webm' });
  stopMeter();
  stopSpeech();
  if (state.stream) state.stream.getTracks().forEach((track) => track.stop());
  $('micBtn').classList.remove('recording');
  setStatus('Recording ready');
}

function startMeter(stream) {
  stopMeter();
  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  if (!AudioCtx) return;
  state.audioContext = new AudioCtx();
  state.analyser = state.audioContext.createAnalyser();
  const source = state.audioContext.createMediaStreamSource(stream);
  source.connect(state.analyser);
  state.analyser.fftSize = 256;
  const data = new Uint8Array(state.analyser.frequencyBinCount);
  const bars = [...document.querySelectorAll('.bar')];
  const tick = () => {
    state.analyser.getByteFrequencyData(data);
    const avg = data.reduce((sum, val) => sum + val, 0) / data.length;
    const pct = Math.min(100, Math.round((avg / 140) * 100));
    $('meterFill').style.width = `${pct}%`;
    bars.forEach((bar, idx) => {
      const val = data[(idx * 3) % data.length] || avg;
      bar.style.height = `${Math.max(4, Math.round((val / 255) * 24))}px`;
      bar.style.opacity = '.9';
    });
    state.meterId = requestAnimationFrame(tick);
  };
  tick();
}

function stopMeter() {
  if (state.meterId) cancelAnimationFrame(state.meterId);
  if (state.audioContext) state.audioContext.close().catch(() => {});
  state.meterId = null;
  state.audioContext = null;
  $('meterFill').style.width = '0';
  document.querySelectorAll('.bar').forEach((bar) => {
    bar.style.height = '5px';
    bar.style.opacity = '.28';
  });
}

function setupSpeechRecognition() {
  const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!SpeechRecognition) return;
  state.recognition = new SpeechRecognition();
  state.recognition.continuous = true;
  state.recognition.interimResults = true;
  state.recognition.lang = 'en-US';
  let committed = '';
  state.recognition.addEventListener('result', (event) => {
    let interim = '';
    for (let i = event.resultIndex; i < event.results.length; i += 1) {
      const text = event.results[i][0].transcript;
      if (event.results[i].isFinal) committed += `${text.trim()} `;
      else interim += text;
    }
    // Use ?? (not ||) — an explicitly-empty dataset.base must NOT fall
    // back to the current textarea value, otherwise we prepend the
    // textarea's existing committed+interim to itself on every event
    // and produce the "ThisThis isThis is a..." doubling pattern.
    const base = $('noteText').dataset.base ?? '';
    $('noteText').value = `${base}${committed}${interim}`.trimStart();
  });
  state.recognition.addEventListener('end', () => {
    $('noteText').dataset.base = $('noteText').value ? `${$('noteText').value.trim()} ` : '';
    committed = '';
  });
}

function startSpeech() {
  if (!state.settings.browserSpeech || !state.recognition) return;
  $('noteText').dataset.base = $('noteText').value ? `${$('noteText').value.trim()} ` : '';
  try { state.recognition.start(); } catch {}
}

function stopSpeech() {
  if (!state.recognition) return;
  try { state.recognition.stop(); } catch {}
}

// Single-flight guard for Save. On slow networks the user used to
// double-tap Save thinking it failed; each tap minted a new UUID and
// created a duplicate capture. Block re-entry until the current save
// settles, and reflect "busy" on the button so the user sees the work.
let savingInFlight = false;

// Screen Wake Lock during saves: keeps the phone screen on through the
// long transcribe/synthesize chain so mobile OSes don't suspend the tab
// mid-request. Entirely best-effort — unsupported browsers and denied
// requests are silently fine (persist-first covers the failure case).
let wakeLock = null;
function acquireWakeLock() {
  if (!('wakeLock' in navigator)) return;
  navigator.wakeLock.request('screen')
    .then((lock) => { wakeLock = lock; })
    .catch(() => {});
}
function releaseWakeLock() {
  try { wakeLock?.release(); } catch {}
  wakeLock = null;
}
// The OS auto-releases the lock when the tab is backgrounded; reacquire
// if the user comes back while a save is still running.
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible' && savingInFlight) acquireWakeLock();
});

// Desktop-grade leave guard: native "unsaved changes" prompt on tab
// close mid-save. Mobile Safari fires this unreliably — persist-first
// (putCapture before any network call) is the real mobile protection.
window.addEventListener('beforeunload', (e) => {
  if (savingInFlight || state.photoImages.length || photoVoiceBlob || photoRecorder?.state === 'recording') {
    e.preventDefault();
    e.returnValue = '';
  }
});

// Service-worker updates must never reload the page out from under an
// in-flight save or an active recording. SW_UPDATED sets the flag;
// maybeApplySwUpdate() runs it once the coast is clear (called from the
// finally blocks of saveSession / savePhotoCapture and on SW message).
let swUpdatePending = false;
function maybeApplySwUpdate() {
  if (!swUpdatePending) return;
  if (savingInFlight) return;
  if (state.recorder && state.recorder.state === 'recording') return;
  if (state.photoImages.length || photoVoiceBlob || photoRecorder?.state === 'recording' || $('photoExplanation')?.value.trim()) return;
  swUpdatePending = false;
  toast('Update available. Reloading…');
  setTimeout(() => {
    // A draft may have started during the update toast's delay.
    if (savingInFlight || state.recorder?.state === 'recording' || state.audioBlob
      || state.photoImages.length || photoVoiceBlob || photoRecorder?.state === 'recording'
      || $('photoExplanation')?.value.trim() || $('noteText')?.value.trim()) {
      swUpdatePending = true;
      return;
    }
    location.reload();
  }, 1500);
}
async function saveSession() {
  if (savingInFlight) return;
  if (!state.capabilities.canCaptureWeb) {
    toast('Your trial has ended. Export your captures anytime.');
    switchTab('library');
    return;
  }
  savingInFlight = true;
  const btn = $('saveSessionBtn');
  if (btn) { btn.disabled = true; btn.classList.add('busy'); }
  acquireWakeLock();
  try {
    if (state.recorder && state.recorder.state === 'recording') {
      state.recorder.stop();
      await delay(250);
    }
    const browserText = $('noteText').value.trim();
    if (!browserText && !state.audioBlob) {
      toast('Record audio or add notes first.');
      return;
    }
    const capture = {
      id: crypto.randomUUID(),
      mode: state.mode || 'freeform',
      topic: state.topic || 'freeform',
      prompt: $('promptText').textContent,
      title: makeTitle(browserText, state.topic),
      text: browserText,
      audio: state.audioBlob,
      audioType: state.audioBlob ? state.audioBlob.type : '',
      // transcribed=false means Whisper hasn't produced (or been given a
      // chance to produce) the authoritative transcript yet. Legacy records
      // lack the field entirely; every check uses strict `=== false` so
      // undefined reads as "already done".
      transcribed: !(state.audioBlob && apiClient.enabled),
      transcribeAttempts: 0,
      audioUploadAttempts: 0,
      cloudSynced: false,
      // synthesizedAt undefined → not yet synthesized.
      // syncAttempts capped in syncPendingCaptures to avoid retry storms.
      syncAttempts: 0,
      createdAt: new Date().toISOString(),
    };
    // Persist BEFORE any network call so closing/backgrounding the tab
    // mid-save can never lose the recording — sync picks it up later.
    // Never drop work the user already made: save even if this capture
    // crosses the local quota. The limit is enforced at capture START
    // (startSession / showPhotoScreen), so this only trips on the one
    // capture that tips over the line — we keep it and gate the next one.
    setSaveStep('saving');
    try {
      await putCapture(capture);
      await loadCaptures();
      renderLibrary();
    } catch (e) {
      // Quota/IDB failure: keep going in-memory — better to reach the
      // cloud than lose the capture entirely. putCapture already toasted.
      captureError(e, { stage: 'saveSession:persistFirst' });
    }
    if (capture.transcribed === false) {
      setSaveStep('transcribing');
      await transcribeCapture(capture);
    }
    setSaveStep('saving');
    const cloudResult = await synthesizeCaptureToCloud(capture, { onStep: setSaveStep });
    if (cloudResult?.ok) {
      await loadFolders();
      state.libTab = 'organized';
      state.folderView = 'root';
    }
    resetCapture();
    switchTab('library');
    toast(cloudResult?.ok ? 'Capture saved and synthesized' : 'Capture saved');
  } finally {
    savingInFlight = false;
    if (btn) { btn.disabled = false; btn.classList.remove('busy'); }
    setSaveStep(null);
    releaseWakeLock();
    renderLibrary();
    maybeApplySwUpdate();
  }
}

// Transcribe a durable capture record via Whisper. Works on the IDB copy
// (capture.audio) so it can run both inside saveSession and from the
// sync retry path (Bucket 0 in syncPendingCaptures). Mutates + persists
// the record; capture.text is the browser transcript until Whisper wins.
const MAX_TRANSCRIBE_ATTEMPTS = 5;
async function transcribeCapture(capture) {
  if (capture.transcribed !== false || !capture.audio || !apiClient.enabled) return;
  capture.transcribeAttempts = (capture.transcribeAttempts || 0) + 1;
  try {
    const ext = audioExtFromType(capture.audioType || capture.audio.type);
    const result = await apiClient.transcribeAudio(capture.audio, `capture-${capture.id}.${ext}`);
    const cloudText = String(result?.text || '').trim();
    if (cloudText) {
      capture.text = cloudText;
      capture.title = makeTitle(cloudText, capture.topic);
      capture.transcribed = true;
      const noteEl = $('noteText');
      if (noteEl) noteEl.value = cloudText;
    }
  } catch (e) {
    captureError(e, { stage: 'transcribeCapture', captureId: capture.id, attempts: capture.transcribeAttempts });
    if (e?.code === 'platform_capacity') {
      // Platform-wide monthly cap — retrying won't help until the 1st.
      // Browser transcript is final.
      capture.transcribed = true;
      toast('Sayermore hit its monthly beta capacity — resets on the 1st. Kept your browser transcript.');
    } else if ((capture.text || '').trim()) {
      // Retryable failure but we have a browser transcript — accept it as
      // final (matches the old UX) rather than blocking the save on Whisper.
      capture.transcribed = true;
      toast('Cloud transcription failed. Using browser transcript.');
    } else if (capture.transcribeAttempts >= MAX_TRANSCRIBE_ATTEMPTS) {
      capture.transcribed = 'failed';
    } else {
      toast('Saved on this device. Transcription will retry.');
    }
  }
  // Whisper can also return empty text without throwing; the record then
  // stays transcribed=false for a later retry until the attempt cap.
  if (capture.transcribed === false && capture.transcribeAttempts >= MAX_TRANSCRIBE_ATTEMPTS) {
    capture.transcribed = 'failed';
  }
  try { await putCapture(capture); } catch {}
}

// Best-effort audio upload for a pushed capture. Reads the durable IDB
// blob (capture.audio), NOT state.audioBlob, so the sync retry path can
// upload audio even after a reload. 402 (not entitled to audio storage)
// sets audioUploadSkipped so we never retry it. Photo captures have
// audio: null and no-op here.
async function uploadCaptureAudio(capture) {
  if (!capture.audio || capture.hasAudio || capture.audioUploadSkipped) return;
  if (!capture.cloudSynced) return; // session row must exist first
  capture.audioUploadAttempts = (capture.audioUploadAttempts || 0) + 1;
  try {
    const ext = audioExtFromType(capture.audioType || capture.audio.type);
    const upload = await apiClient.uploadAudio(capture.id, capture.audio, `capture-${capture.id}.${ext}`);
    if (upload?.ok) {
      capture.hasAudio = true;
      toast('Audio saved.');
    }
  } catch (e) {
    if (e?.status === 402) capture.audioUploadSkipped = true;
    captureError(e, { stage: 'uploadCaptureAudio', captureId: capture.id, attempts: capture.audioUploadAttempts });
  }
  try { await putCapture(capture); } catch {}
}

// Sync a single capture to the cloud in two decoupled phases:
//   Phase 1: pushSession — persist the transcript. Mark cloudSynced=true
//            as soon as this succeeds, regardless of phase 2.
//   Phase 2: synthesize (and best-effort audio upload). On success, set
//            synthesizedAt. On failure, leave synthesizedAt undefined so
//            syncPendingCaptures can retry synthesis ONLY — without
//            re-pushing the session row.
//
// This stops the retry-storm pattern where a synthesis timeout on a long
// transcript caused the whole chain (push + audio upload + synthesize)
// to retry on every app load, re-uploading megabytes of audio.
async function synthesizeCaptureToCloud(capture, { onStep } = {}) {
  if (!apiClient.enabled || !capture.text?.trim()) return { ok: false, skipped: true };
  // Phase 1: push the session row. If this fails, the entire flow is
  // aborted and the capture stays cloudSynced=false for a later retry.
  try {
    await apiClient.pushSession({
      id: capture.id,
      transcript: capture.text,
      mode: capture.mode === 'guided' ? 'guided' : 'freeform',
      topic: capture.topic || null,
      createdAt: capture.createdAt,
    });
    capture.cloudSynced = true;
    try { await putCapture(capture); } catch {}
  } catch (e) {
    captureError(e, { stage: 'synthesizeCaptureToCloud:pushSession', captureId: capture.id });
    return { ok: false, error: e, pushed: false };
  }

  // Phase 2: best-effort audio upload (paid feature; 402 if not entitled).
  await uploadCaptureAudio(capture);

  // Phase 3: synthesis (the expensive Claude Opus call). On failure, the
  // capture stays cloudSynced=true but synthesizedAt remains undefined —
  // syncPendingCaptures will retry just this step next time.
  onStep?.('organizing');
  try {
    const result = await apiClient.synthesize({
      transcript: capture.text,
      hint: synthesisHintFor(capture),
      sessionId: capture.id,
      displayName: currentDisplayName(),
    });
    if (!organizationSucceeded(result)) {
      return { ok: false, skipped: true, pushed: true };
    }
    capture.synthesizedAt = new Date().toISOString();
    try { await putCapture(capture); } catch {}
    return { ok: true, operations: result.operations, note: result.note, results: result.results };
  } catch (e) {
    captureError(e, { stage: 'synthesizeCaptureToCloud:synthesize', captureId: capture.id });
    toast('Saved. Synthesis will retry.');
    return { ok: false, error: e, pushed: true };
  }
}

// Synthesis-only retry path. Skips pushSession and audio upload; just
// re-attempts the Claude call. Bumps syncAttempts; marks as 'failed'
// after MAX_SYNTHESIS_ATTEMPTS so we stop retrying forever.
const MAX_SYNTHESIS_ATTEMPTS = 5;
async function retrySynthesisOnly(capture) {
  if (!apiClient.enabled) return false;
  const attempts = (capture.syncAttempts || 0) + 1;
  capture.syncAttempts = attempts;
  try {
    const result = await apiClient.synthesize({
      transcript: capture.text,
      hint: synthesisHintFor(capture),
      sessionId: capture.id,
      displayName: currentDisplayName(),
    });
    if (organizationSucceeded(result)) {
      capture.synthesizedAt = new Date().toISOString();
      try { await putCapture(capture); } catch {}
      return true;
    }
  } catch (e) {
    captureError(e, { stage: 'retrySynthesisOnly', id: capture.id, attempts });
  }
  if (attempts >= MAX_SYNTHESIS_ATTEMPTS) {
    capture.synthesizedAt = 'failed';
  }
  try { await putCapture(capture); } catch {}
  return false;
}

function resetCapture() {
  if (state.recorder && state.recorder.state === 'recording') state.recorder.stop();
  if (state.stream) state.stream.getTracks().forEach((track) => track.stop());
  // Release the in-memory recording. The durable copy lives on the
  // capture record in IDB (capture.audio), which the upload/retry paths
  // read — this only drops the state reference.
  state.audioBlob = null;
  state.chunks = [];
  stopMeter();
  stopSpeech();
  $('micBtn').classList.remove('recording');
  // After saving/discarding an audio session, return to the Audio | Photo
  // chooser rather than immediately re-opening the mic.
  showCaptureChooser();
}

// Discard-session flow: the Discard button is destructive (drops audio
// + transcript). If the user has opted into "don't ask again", we skip
// the modal and call resetCapture directly. Otherwise the modal lets
// them confirm, with an inline checkbox that turns future prompts off.
function onDiscardSessionTap() {
  if (state.settings.confirmDiscard === false) {
    resetCapture();
    return;
  }
  const modal = $('discardModal');
  if (!modal) { resetCapture(); return; }
  const skipBox = $('discardSkipBox');
  if (skipBox) skipBox.checked = false;
  modal.classList.add('open');
  modal.setAttribute('aria-hidden', 'false');
}

function closeDiscardModal() {
  const modal = $('discardModal');
  if (!modal) return;
  modal.classList.remove('open');
  modal.setAttribute('aria-hidden', 'true');
}

function onDiscardConfirm() {
  const skipBox = $('discardSkipBox');
  if (skipBox && skipBox.checked) {
    state.settings.confirmDiscard = false;
    try { localStorage.setItem('sayermore.settings', JSON.stringify(state.settings)); } catch {}
  }
  closeDiscardModal();
  resetCapture();
}

// ---- Capture entry chooser + photo capture ------------------------------

const MAX_PHOTO_IMAGES = 8;

// Synthesis hint tells Claude how to treat the text. Photo captures are
// already-structured text from paper, not a spoken stream, so flag that.
function synthesisHintFor(capture) {
  if (capture.mode === 'photo') {
    return 'Photo memory with personal context. Keep user statements separate from AI visual observations.';
  }
  return `${labelFor(capture.topic)} capture from the Sayermore web app.`;
}

// Land on the Audio | Photo chooser and clear any pending photo state.
function showCaptureChooser() {
  if (savingInFlight) return;
  if ((state.photoImages.length || photoVoiceBlob) && !confirm('Discard this unsaved photo memory?')) return;
  resetPhotoCapture();
  showScreen('captureChoiceScreen');
}

function showPhotoScreen() {
  if (savingInFlight) return;
  if (!state.capabilities.canCaptureWeb) {
    toast('Your trial has ended. Export your captures anytime, or subscribe to keep using Sayermore Cloud.');
    switchTab('library');
    return;
  }
  if (!apiClient.enabled) {
    toast('Sign in to capture and synthesize photos.');
    return;
  }
  if (isStorageFull()) { showStorageLimitModal(); return; }
  // Preserve an unfinished photo draft when switching tabs.
  // Premium gate: a stale storeImage=true (set before the gate existed, or
  // from a lapsed subscription) must not silently keep uploading images.
  if (state.settings.storeImage && state.planStatus !== 'paid_active') {
    state.settings.storeImage = false;
    try { localStorage.setItem('sayermore.settings', JSON.stringify(state.settings)); } catch {}
  }
  const toggle = $('storeImageToggle');
  if (toggle) toggle.checked = Boolean(state.settings.storeImage);
  showScreen('photoScreen');
}

function onPhotoFilesAdded(fileList) {
  if (savingInFlight) return;
  const incoming = Array.from(fileList || []).filter((f) => f && f.type && f.type.startsWith('image/'));
  if (!incoming.length) return;
  for (const f of incoming) {
    if (!['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(f.type) || f.size > 10 * 1024 * 1024) {
      toast('Use JPEG, PNG, WebP, or GIF photos under 10 MB. Export HEIC as JPEG first.');
      continue;
    }
    if (state.photoImages.length >= MAX_PHOTO_IMAGES) {
      toast(`Up to ${MAX_PHOTO_IMAGES} photos per memory.`);
      break;
    }
    state.photoImages.push(f);
  }
  // Allow re-selecting the same file again later (e.g. retake).
  const cam = $('photoCameraInput'); if (cam) cam.value = '';
  const up = $('photoUploadInput'); if (up) up.value = '';
  renderPhotoThumbs();
}

function removePhotoImage(i) {
  if (savingInFlight) return;
  state.photoImages.splice(i, 1);
  renderPhotoThumbs();
}

// Build thumbnails from object URLs. Content is image data + our own
// numbering only — no user/AI text — so DOM construction here is XSS-safe.
function renderPhotoThumbs() {
  const wrap = $('photoThumbs');
  if (!wrap) return;
  wrap.querySelectorAll('img[data-url]').forEach((img) => {
    try { URL.revokeObjectURL(img.getAttribute('data-url')); } catch {}
  });
  wrap.innerHTML = '';
  state.photoImages.forEach((f, i) => {
    const url = URL.createObjectURL(f);
    const cell = document.createElement('div');
    cell.className = 'photo-thumb';
    const img = document.createElement('img');
    img.src = url;
    img.setAttribute('data-url', url);
    img.alt = `Photo ${i + 1}`;
    const rm = document.createElement('button');
    rm.className = 'photo-thumb-remove';
    rm.type = 'button';
    rm.setAttribute('aria-label', `Remove photo ${i + 1}`);
    rm.innerHTML = '<i class="fa-solid fa-xmark"></i>';
    rm.addEventListener('click', () => removePhotoImage(i));
    cell.appendChild(img);
    cell.appendChild(rm);
    wrap.appendChild(cell);
  });
  setPhotoStatus();
}

function setPhotoStatus(text) {
  const el = $('photoStatus');
  if (!el) return;
  if (text) { el.textContent = text; return; }
  const n = state.photoImages.length;
  el.textContent = n === 0 ? 'No photos added yet' : `${n} photo${n === 1 ? '' : 's'} ready`;
}

function resetPhotoCapture() {
  clearPhotoVoice();
  if ($('photoExplanation')) $('photoExplanation').value = '';
  const wrap = $('photoThumbs');
  if (wrap) {
    wrap.querySelectorAll('img[data-url]').forEach((img) => {
      try { URL.revokeObjectURL(img.getAttribute('data-url')); } catch {}
    });
  }
  state.photoImages = [];
  const cam = $('photoCameraInput'); if (cam) cam.value = '';
  const up = $('photoUploadInput'); if (up) up.value = '';
  renderPhotoThumbs();
}

function onStoreImageToggle(e) {
  // Storing original images is premium-only (image storage costs R2 money).
  if (e.target.checked && state.planStatus !== 'paid_active') {
    e.target.checked = false;
    toast('Storing original images is a premium feature. Subscribe to keep your photos alongside the extracted text.');
    return;
  }
  state.settings.storeImage = Boolean(e.target.checked);
  try { localStorage.setItem('sayermore.settings', JSON.stringify(state.settings)); } catch {}
}

// Photo recording is independent of the standalone audio capture state.
let photoRecorder = null;
let photoStream = null;
let photoVoiceBlob = null;
let photoVoiceUrl = null;
let photoStopPromise = null;
let photoRecordingTimer = null;
let photoMicGeneration = 0;
let photoMicOpening = false;
let photoDetailUrls = [];
const photoSyncJobs = new Map();

function clearPhotoVoice() {
  photoMicGeneration++;
  photoMicOpening = false;
  clearTimeout(photoRecordingTimer);
  if (photoRecorder) {
    photoRecorder.onstop = null;
    if (photoRecorder.state !== 'inactive') photoRecorder.stop();
  }
  photoStream?.getTracks().forEach((track) => track.stop());
  photoStream = null;
  photoRecorder = null;
  photoVoiceBlob = null;
  photoStopPromise = null;
  if (photoVoiceUrl) URL.revokeObjectURL(photoVoiceUrl);
  photoVoiceUrl = null;
  const preview = $('photoVoicePreview');
  if (preview) { preview.pause(); preview.removeAttribute('src'); preview.hidden = true; }
  if ($('photoVoiceRemove')) $('photoVoiceRemove').hidden = true;
  if ($('photoVoiceBtn')) { $('photoVoiceBtn').disabled = false; $('photoVoiceBtn').textContent = 'Record explanation'; }
  if ($('photoVoiceStatus')) $('photoVoiceStatus').textContent = 'Speak instead of typing, or use both.';
}

async function togglePhotoVoice() {
  if (savingInFlight || photoMicOpening) return;
  if (photoRecorder?.state === 'recording') { await stopPhotoVoice(); return; }
  if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
    toast('Recording is unavailable in this browser. Type an explanation instead.'); return;
  }
  clearPhotoVoice();
  const generation = photoMicGeneration;
  photoMicOpening = true;
  $('photoVoiceBtn').disabled = true;
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    if (generation !== photoMicGeneration || state.activeTab !== 'capture' || !$('photoScreen').classList.contains('active')) {
      stream.getTracks().forEach((track) => track.stop()); return;
    }
    photoStream = stream;
    const recorder = new MediaRecorder(stream);
    photoRecorder = recorder;
    const chunks = [];
    recorder.ondataavailable = (event) => { if (event.data.size) chunks.push(event.data); };
    photoStopPromise = new Promise((resolve) => {
      recorder.onstop = () => {
        clearTimeout(photoRecordingTimer);
        stream.getTracks().forEach((track) => track.stop());
        photoStream = null;
        photoRecorder = null;
        photoVoiceBlob = new Blob(chunks, { type: recorder.mimeType || chunks[0]?.type || 'audio/webm' });
        if (photoVoiceBlob.size) {
          photoVoiceUrl = URL.createObjectURL(photoVoiceBlob);
          $('photoVoicePreview').src = photoVoiceUrl;
          $('photoVoicePreview').hidden = false;
          $('photoVoiceRemove').hidden = false;
          $('photoVoiceStatus').textContent = 'Recording ready. It will be transcribed when you save.';
        } else { photoVoiceBlob = null; $('photoVoiceStatus').textContent = 'No audio recorded. Try again or type your explanation.'; }
        $('photoVoiceBtn').textContent = 'Record again';
        resolve();
      };
    });
    recorder.onerror = () => { stopPhotoVoice(); toast('Recording interrupted. Check the playback before saving.'); };
    recorder.start(1000);
    $('photoVoiceBtn').textContent = 'Stop recording';
    $('photoVoiceStatus').textContent = 'Recording your explanation… (up to 2 minutes)';
    photoRecordingTimer = setTimeout(() => stopPhotoVoice(), 120000);
  } catch {
    photoStream?.getTracks().forEach((track) => track.stop());
    photoStream = null;
    $('photoVoiceStatus').textContent = 'Microphone unavailable. Allow microphone access or type your explanation.';
  } finally {
    photoMicOpening = false;
    $('photoVoiceBtn').disabled = false;
  }
}

async function stopPhotoVoice() {
  if (photoRecorder?.state === 'recording') photoRecorder.stop();
  if (photoStopPromise) await photoStopPromise;
}

function processPhotoMemory(capture, onStep, retry = false) {
  if (photoSyncJobs.has(capture.id)) return photoSyncJobs.get(capture.id);
  const job = syncPhotoMemory(capture, { api: apiClient, persist: putCapture, onStep, retry, displayName: currentDisplayName() })
    .finally(() => photoSyncJobs.delete(capture.id));
  photoSyncJobs.set(capture.id, job);
  return job;
}

async function savePhotoCapture() {
  if (savingInFlight || photoMicOpening) return;
  if (!state.capabilities.canCaptureWeb) { toast('Capture is unavailable on this account.'); return; }
  if (!apiClient.enabled) { toast('Sign in to save photo memories.'); return; }
  if (!state.photoImages.length) { toast('Add a photo first.'); return; }
  savingInFlight = true;
  const btn = $('photoSynthBtn');
  btn.disabled = true;
  $('photoExplanation').disabled = true;
  $('photoVoiceBtn').disabled = true;
  $('photoVoiceRemove').disabled = true;
  acquireWakeLock();
  let locallySaved = false;
  try {
    await stopPhotoVoice();
    const capture = {
      id: crypto.randomUUID(), mode: 'photo', topic: 'freeform', prompt: '',
      title: $('photoExplanation').value.trim().slice(0, 90) || 'Photo memory',
      explanation: $('photoExplanation').value.trim(),
      images: state.photoImages.slice(), voiceAudio: photoVoiceBlob,
      storeImages: Boolean($('storeImageToggle').checked),
      audio: null, audioType: '', cloudSynced: false, createdAt: new Date().toISOString(),
    };
    capture.text = photoMemoryText(capture);
    // Originals are durable before transcription, vision, or uploads start.
    await putCapture(capture);
    locallySaved = true;
    setPhotoStatus('Saved on this device. Preparing your searchable memory…');
    await processPhotoMemory(capture, setPhotoStatus);
    await loadCaptures();
    await loadFolders();
    resetPhotoCapture();
    switchTab('library');
    openCapture(capture.id);
    toast(capture.backgroundJobId ? 'Uploaded. You can close the app while processing finishes.' : 'Saved on this device. Upload is still pending; retry from this memory.');
  } catch (error) {
    captureError(error, { stage: 'savePhotoCapture' });
    setPhotoStatus(locallySaved ? 'Saved on this device. Open Library to retry sync.' : 'Could not save on this device. Your draft is still here; free space and try again.');
  } finally {
    savingInFlight = false;
    btn.disabled = false;
    $('photoExplanation').disabled = false;
    $('photoVoiceBtn').disabled = false;
    $('photoVoiceRemove').disabled = false;
    releaseWakeLock();
    maybeApplySwUpdate();
  }
}

function renderPhotoDetail(c) {
  const body = $('captureBody');
  const host = document.createElement('div');
  host.className = 'memory-originals';
  const status = document.createElement('p');
  status.className = 'photo-storage-note';
  status.textContent = c.hasImages ? 'Original photos synced to your account.'
    : c.images?.length ? 'Original photos saved on this device only. Download them below or export a backup before clearing browser data.'
    : 'Original photos were not synced from the device where this was captured.';
  host.appendChild(status);
  if (c.backgroundJobId || c.photoSyncError || c.imageSyncError) {
    const warning = document.createElement('p');
    warning.className = 'photo-storage-note';
    warning.textContent = c.backgroundJobId ? photoJobLabel(c) : c.cloudSynced
      ? 'Your description is searchable. Photo sync or organization is still pending.'
      : 'Saved on this device. Transcription or sync is still pending; Ask cannot find this memory yet.';
    host.appendChild(warning);
  }
  const gallery = document.createElement('div');
  gallery.className = 'memory-photo-grid';
  host.appendChild(gallery);
  const addPhoto = (url, i, local = false) => {
    const figure = document.createElement('figure');
    const img = document.createElement('img');
    img.src = url; img.alt = `Original photo ${i + 1}`; img.loading = 'lazy';
    figure.appendChild(img);
    if (local) {
      const link = document.createElement('a');
      link.href = url; link.download = c.images[i].name || `memory-photo-${i + 1}.jpg`;
      link.textContent = `Download photo ${i + 1}`;
      figure.appendChild(link);
    }
    gallery.appendChild(figure);
  };
  if (c.images?.length) {
    c.images.forEach((blob, i) => { const url = URL.createObjectURL(blob); photoDetailUrls.push(url); addPhoto(url, i, true); });
  } else if (c.hasImages) {
    apiClient.getImageUrls(c.id).then((urls) => {
      if (!host.isConnected || state.currentCapture?.id !== c.id) return;
      urls.forEach((url, i) => { if (/^https:\/\//.test(url)) addPhoto(url, i); });
      if (!urls.length) status.textContent = 'Photos could not be loaded. Reopen this memory to try again.';
    }).catch(() => { if (host.isConnected) status.textContent = 'Photos could not be loaded. Reopen this memory to try again.'; });
  }
  if (c.voiceAudio) {
    const label = document.createElement('p'); label.textContent = 'Your original voice explanation · saved on this device';
    const audio = document.createElement('audio'); audio.controls = true;
    audio.src = URL.createObjectURL(c.voiceAudio); photoDetailUrls.push(audio.src);
    host.append(label, audio);
  }
  body.prepend(host);
  const ask = document.createElement('button');
  ask.className = 'pill primary'; ask.textContent = 'Ask about this memory';
  ask.disabled = !c.cloudSynced;
  ask.addEventListener('click', () => {
    switchTab('ask');
    $('askInput').value = `What do I remember about ${c.title}?`;
    $('askInput').focus(); autoGrowAskInput();
  });
  $('captureActions').prepend(ask);
  if (c.images?.length) {
    const backup = document.createElement('button');
    backup.className = 'pill'; backup.textContent = 'Export memory';
    backup.addEventListener('click', async () => {
      backup.disabled = true;
      try {
        const zip = new JSZip();
        zip.file('memory.md', `# ${c.title}\n\nCaptured: ${c.createdAt}\n\n${c.text || c.explanation || ''}`);
        for (const [i, blob] of c.images.entries()) {
          const ext = ({ 'image/jpeg': 'jpg', 'image/png': 'png', 'image/webp': 'webp', 'image/gif': 'gif' })[blob.type] || 'jpg';
          zip.file(`photo-${i + 1}.${ext}`, await blob.arrayBuffer());
        }
        if (c.voiceAudio) zip.file(c.voiceAudio.type.includes('mp4') ? 'voice-explanation.m4a' : 'voice-explanation.webm', await c.voiceAudio.arrayBuffer());
        triggerDownload(await zip.generateAsync({ type: 'blob' }), `${sanitizeFilename(c.title)}.zip`);
      } catch { toast('Export failed. Try downloading the photos individually.'); }
      finally { backup.disabled = false; }
    });
    $('captureActions').appendChild(backup);
  }
  if ((!c.backgroundJobId && c.images?.length && (!c.cloudSynced || !c.synthesizedAt || c.photoSyncError)) || c.processingStatus === 'failed') {
    const retry = document.createElement('button');
    retry.className = 'pill'; retry.textContent = 'Retry memory sync';
    retry.addEventListener('click', async () => {
      if (savingInFlight) return;
      retry.disabled = true;
      try {
        await processPhotoMemory(c, (label) => { retry.textContent = label; }, true);
        await loadCaptures(); await loadFolders();
        if (state.currentCapture?.id === c.id) { state.currentCapture = state.captures.find((item) => item.id === c.id); renderCaptureDetail(); }
      } catch { retry.disabled = false; retry.textContent = 'Retry memory sync'; }
    });
    $('captureActions').appendChild(retry);
  }
}

function renderLibrary() {
  const folderCount = state.folders.length;
  const captureCount = state.captures.length;
  // Suppress count + storage signal in the signed-out-with-real-data case
  // so it doesn't look like the visitor is browsing someone else's data.
  const inDemo = !isSignedIn() && (typeof localStorage !== 'undefined') && localStorage.getItem(DEMO_SEEDED_KEY) === '1';
  const hideSignedOut = isAuthConfigured() && !isSignedIn() && !inDemo && state.captures.some((c) => c.demo !== true);
  // One-time auto-default: if Organized is empty but captures exist, land on
  // Sessions so the user sees their content instead of a dead-end empty state.
  if (!state.libTabAutoSet && state.libTab === 'organized' && folderCount === 0 && captureCount > 0 && !hideSignedOut) {
    state.libTab = 'sessions';
    state.libTabAutoSet = true;
  }
  if (hideSignedOut) {
    $('libraryStats').textContent = '';
  } else {
    const parts = [];
    if (folderCount) parts.push(`${folderCount} folder${folderCount === 1 ? '' : 's'}`);
    parts.push(`${captureCount} capture${captureCount === 1 ? '' : 's'}`);
    $('libraryStats').textContent = parts.join(' · ');
  }
  const storageCard = $('storageCard');
  if (storageCard) storageCard.style.display = hideSignedOut ? 'none' : '';
  if (!hideSignedOut) renderStorage();
  applyLibTab();
  if (state.libTab === 'organized') {
    if (state.folderView === 'doc') renderDocView();
    else if (state.folderView === 'folder') renderFolderView();
    else renderRoot();
  } else if (state.libTab === 'reflections') {
    renderReflectionsView();
  } else if (state.sessionsView === 'detail') {
    renderCaptureDetail();
  } else {
    renderSessions();
  }
}

function applyLibTab() {
  document.querySelectorAll('[data-libtab]').forEach((btn) => btn.classList.toggle('active', btn.dataset.libtab === state.libTab));
  const organized = state.libTab === 'organized';
  const sessions = state.libTab === 'sessions';
  const reflections = state.libTab === 'reflections';
  $('libTabs').style.display = (organized && state.folderView !== 'root') || (sessions && state.sessionsView !== 'list') ? 'none' : 'flex';
  $('organizedView').classList.toggle('active', organized && state.folderView === 'root');
  $('folderView').classList.toggle('active', organized && state.folderView === 'folder');
  $('docView').classList.toggle('active', organized && state.folderView === 'doc');
  $('sessionsView').classList.toggle('active', sessions && state.sessionsView === 'list');
  $('captureDetailView').classList.toggle('active', sessions && state.sessionsView === 'detail');
  const reflectionsView = document.getElementById('reflectionsView');
  if (reflectionsView) reflectionsView.classList.toggle('active', reflections);
}

function setLibTab(tab) {
  state.libTab = tab;
  state.libTabAutoSet = true;
  state.folderView = 'root';
  state.sessionsView = 'list';
  state.currentFolder = null;
  state.currentDoc = null;
  state.currentCapture = null;
  renderLibrary();
  recordNav();
}

async function loadCloudSessions() {
  if (!apiClient.enabled) return;
  let sessions;
  try {
    sessions = await apiClient.listSessions();
  } catch (e) {
    captureError(e, { stage: 'loadCloudSessions' });
    return;
  }
  if (!Array.isArray(sessions) || sessions.length === 0) return;
  const localById = new Map(state.captures.map((c) => [c.id, c]));
  let added = 0;
  let updated = 0;
  for (const s of sessions) {
    if (!s || !s.id) continue;
    const existing = localById.get(s.id);
    if (existing) {
      // Already in IDB. Refresh the hasAudio flag in case it changed
      // server-side (e.g. user just paid for audio storage).
      if (Boolean(existing.hasAudio) !== Boolean(s.hasAudio) || Boolean(existing.hasImages) !== Boolean(s.hasImages) || (s.processingJob && !existing.backgroundJobId)) {
        if (s.processingJob) existing.backgroundJobId = s.id;
        existing.hasImages = Boolean(s.hasImages);
        existing.hasAudio = Boolean(s.hasAudio);
        try { await putCapture(existing); updated += 1; } catch {}
      }
      continue;
    }
    const text = s.transcript || '';
    const createdAt = s.createdAt || s.created_at || new Date().toISOString();
    try {
      await putCapture({
        id: s.id,
        mode: s.mode || 'freeform',
        topic: s.topic || 'freeform',
        prompt: '',
        title: makeTitle(text, s.topic),
        text,
        audio: null,
        audioType: '',
        cloudSynced: true,
        hasAudio: Boolean(s.hasAudio),
        hasImages: Boolean(s.hasImages),
        backgroundJobId: s.processingJob ? s.id : undefined,
        createdAt,
      });
      added += 1;
    } catch (e) {
      captureError(e, { stage: 'loadCloudSessions:putCapture', id: s.id });
    }
  }
  if (added > 0 || updated > 0) await loadCaptures();
}

// Network state helpers (Piece 5).
//
// shouldSyncNow gates cloud calls. Three layers:
//   1. navigator.onLine — always check; if offline, skip.
//   2. syncWifiOnly setting — if false, allow on any network.
//   3. NetworkInformation API — when available, check connection.type
//      or effectiveType. When unavailable (Firefox, older Safari),
//      best-effort: assume WiFi (don't block uploads on platforms we
//      can't query).
function isOnline() {
  return typeof navigator !== 'undefined' && navigator.onLine !== false;
}
function detectConnectionType() {
  if (typeof navigator === 'undefined') return 'unknown';
  const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (!conn) return 'unknown';
  if (conn.type) return conn.type; // 'wifi', 'cellular', 'ethernet', 'none', etc.
  if (conn.effectiveType) {
    return conn.effectiveType === '4g' || conn.effectiveType === '3g' || conn.effectiveType === '2g'
      ? 'cellular' : 'wifi';
  }
  return 'unknown';
}
function shouldSyncNow() {
  if (!isOnline()) return false;
  if (!state.settings.syncWifiOnly) return true;
  const t = detectConnectionType();
  // On platforms that can't detect, fall through (assume OK).
  if (t === 'unknown') return true;
  return t === 'wifi' || t === 'ethernet';
}

// Auto-resume: when the network changes from cellular → wifi (or
// offline → online), kick off any pending sync work.
function bindNetworkAutoResume() {
  if (typeof window === 'undefined') return;
  const rerun = () => {
    if (shouldSyncNow()) {
      syncPendingCaptures().catch((e) => captureError(e, { stage: 'syncPendingCaptures:autoresume' }));
    }
  };
  window.addEventListener('online', rerun);
  const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (conn && typeof conn.addEventListener === 'function') {
    conn.addEventListener('change', rerun);
  }
}

// Manual-trigger entrypoint. Bypasses the WiFi-only gate so the user
// can force a sync from the pill or Settings even on cellular. Bound
// to #pendingSyncPill and #syncNowBtn.
async function onSyncNowClick() {
  if (!apiClient.enabled) {
    toast('Sign in to sync your captures.');
    return;
  }
  if (savingInFlight) {
    toast('Still saving your capture — hang tight.');
    return;
  }
  // Network-aware check before forcing a sync:
  //   - 'wifi' / 'ethernet': proceed silently (best case).
  //   - 'cellular': confirm() — user may not want to burn mobile data,
  //     especially on long-transcript captures with synthesis retries.
  //   - 'none': offline, abort with a useful toast.
  //   - 'unknown': platform can't detect; proceed silently rather than
  //     annoy users with a "we don't know what network you're on" prompt.
  const ct = detectConnectionType();
  if (ct === 'none') {
    toast("You're offline. Connect to a network first.");
    return;
  }
  if (ct === 'cellular') {
    const pendingCount = state.captures.filter((c) => c.demo !== true && c.cloudSynced !== true && (c.text || '').trim()).length;
    const msg = pendingCount > 0
      ? `You're on cellular, not WiFi. Sync ${pendingCount} pending capture${pendingCount === 1 ? '' : 's'} now? This may use mobile data.`
      : "You're on cellular, not WiFi. Sync now? This may use mobile data.";
    if (!confirm(msg)) return;
  }
  const pill = $('pendingSyncPill');
  if (pill) pill.classList.add('syncing');
  const syncBtn = $('syncNowBtn');
  if (syncBtn) { syncBtn.disabled = true; syncBtn.classList.add('busy'); }
  try {
    await syncPendingCaptures({ force: true });
    await loadFolders();
    renderLibrary();
    toast('Sync complete.');
  } catch (e) {
    captureError(e, { stage: 'onSyncNowClick' });
    toast('Sync failed. Try again in a moment.');
  } finally {
    if (pill) pill.classList.remove('syncing');
    if (syncBtn) { syncBtn.disabled = false; syncBtn.classList.remove('busy'); }
  }
}

async function syncPendingCaptures({ force = false } = {}) {
  if (!apiClient.enabled) return;
  // WiFi-only gate (Piece 5). If the user opted into "Sync only on WiFi"
  // AND the platform reports a non-WiFi connection, skip this pass.
  // shouldSyncNow returns true on platforms that can't detect (best-effort).
  // `force: true` (from onSyncNowClick) bypasses the gate so the user
  // can manually push even on cellular.
  if (!force && !shouldSyncNow()) return;
  // A save is mid-flight: its capture is already in IDB with
  // cloudSynced=false, so a concurrent sync pass would double-push it.
  // saveSession finishes its own chain; we pick up any leftovers later.
  if (savingInFlight) return;

  const photos = state.captures.filter((c) => c.mode === 'photo' && (
    photoJobPending(c) || (!c.backgroundJobId && c.images?.length && (!c.cloudSynced || !c.synthesizedAt)
      && (force || (c.photoSyncAttempts || 0) < 5))));
  for (const capture of photos) await processPhotoMemory(capture);


  const eligible = state.captures.filter((c) =>
    c.mode !== 'photo' && c.demo !== true
    && ((c.text || '').trim() || (c.audio && c.transcribed === false)),
  );

  // Bucket 0: recorded but never transcribed (persist-first saves that
  // died before Whisper finished, or Whisper kept failing with no
  // browser transcript to fall back on).
  const pendingTranscribe = eligible.filter((c) =>
    c.transcribed === false
    && c.audio
    && (c.transcribeAttempts || 0) < MAX_TRANSCRIBE_ATTEMPTS,
  );
  for (const capture of pendingTranscribe) {
    await transcribeCapture(capture);
  }

  // Bucket 1: never pushed. Run the full chain (push + audio + synth).
  // Recompute after Bucket 0 so freshly transcribed captures flow through
  // in the same pass. Captures still awaiting transcription with no text
  // have nothing to push yet.
  const pendingPush = eligible.filter((c) =>
    c.cloudSynced !== true
    && (c.text || '').trim()
    && c.transcribed !== false,
  );
  for (const capture of pendingPush) {
    await synthesizeCaptureToCloud(capture);
  }

  // Bucket 2: pushed but synthesis hasn't succeeded yet. Re-run synthesis
  // ONLY. Skip captures that have failed too many times (synthesizedAt
  // === 'failed') so we don't retry forever.
  const pendingSynth = eligible.filter((c) =>
    c.cloudSynced === true
    && !c.synthesizedAt
    && (c.syncAttempts || 0) < MAX_SYNTHESIS_ATTEMPTS,
  );
  for (const capture of pendingSynth) {
    await retrySynthesisOnly(capture);
  }

  // Bucket 3: pushed, has audio, but the audio never made it to R2
  // (upload failed or the tab died first). uploadCaptureAudio reads the
  // durable IDB blob, so this works across reloads.
  const pendingAudio = eligible.filter((c) =>
    c.cloudSynced === true
    && c.audio
    && !c.hasAudio
    && !c.audioUploadSkipped
    && (c.audioUploadAttempts || 0) < MAX_TRANSCRIBE_ATTEMPTS,
  );
  for (const capture of pendingAudio) {
    await uploadCaptureAudio(capture);
  }
}

async function loadFolders() {
  try {
    state.folders = await apiClient.listFolders();
  } catch (e) {
    captureError(e, { stage: 'loadFolders' });
    state.folders = [];
  }
}

function renderRoot() {
  const list = state.folders;
  const inDemo = !isSignedIn() && (typeof localStorage !== 'undefined') && localStorage.getItem(DEMO_SEEDED_KEY) === '1';
  // "Sign in to see your synced library" only kicks in when the visitor
  // is signed out AND has no demo data to look at. Otherwise we want to
  // show whatever folders are present (demo or real).
  const signedOut = isAuthConfigured() && !isSignedIn() && !inDemo;
  const orgEmpty = $('folderEmpty');
  const orgSignedOut = $('organizedSignedOut');
  if (signedOut) {
    if (orgEmpty) orgEmpty.style.display = 'none';
    if (orgSignedOut) orgSignedOut.style.display = 'block';
    $('folderList').innerHTML = '';
  } else {
    if (orgSignedOut) orgSignedOut.style.display = 'none';
    if (orgEmpty) orgEmpty.style.display = list.length ? 'none' : 'block';
    $('folderList').innerHTML = list.map(folderCard).join('');
    document.querySelectorAll('.folder-item').forEach((item) => item.addEventListener('click', () => openFolder(item.dataset.key)));
  }
}

function folderCard(f, i) {
  const tone = FOLDER_TONES[i % FOLDER_TONES.length];
  const count = f.docCount || 0;
  return `<button class="folder-item" data-key="${escapeAttr(f.key)}">
    <span class="folder-icon ${tone}"><i class="fa-solid fa-folder"></i></span>
    <span class="folder-info">
      <span class="folder-name">${escapeHtml(f.title || f.key)}</span>
      <span class="folder-meta">${escapeHtml(f.description || '')}</span>
    </span>
    <span class="folder-count">${count} doc${count === 1 ? '' : 's'}</span>
    <span class="folder-arrow"><i class="fa-solid fa-chevron-right"></i></span>
  </button>`;
}

async function openFolder(key) {
  state.folderView = 'folder';
  try {
    const result = await apiClient.listDocsInFolder(key);
    state.currentFolder = result.folder || { key, title: key, description: '' };
    state.currentDocs = result.docs || [];
  } catch (e) {
    captureError(e, { stage: 'openFolder', key });
    state.currentFolder = { key, title: key, description: '' };
    state.currentDocs = [];
  }
  renderLibrary();
  recordNav();
}

function renderFolderView() {
  const folder = state.currentFolder || { key: '', title: '' };
  $('crumbFolder').textContent = folder.title || folder.key;
  $('folderTitle').textContent = folder.title || folder.key;
  $('docList').innerHTML = state.currentDocs.map(docCard).join('') || '<div class="empty empty-inline"><p>This folder is empty.</p></div>';
  document.querySelectorAll('.doc-item').forEach((item) => item.addEventListener('click', (event) => {
    const deleteTarget = event.target.closest('[data-delete-key]');
    if (deleteTarget) {
      event.stopPropagation();
      onDeleteDocFromList(folder.key, deleteTarget.dataset.deleteKey);
      return;
    }
    openDoc(folder.key, item.dataset.key);
  }));
  const folderActions = $('folderActions');
  if (folderActions) {
    folderActions.innerHTML = `
      <button class="pill" id="folderExport"><i class="fa-solid fa-file-zipper"></i> Export folder</button>
      <button class="pill danger" id="folderDelete"><i class="fa-solid fa-trash"></i> Delete folder</button>`;
    $('folderExport').addEventListener('click', () => onExportFolder(folder));
    $('folderDelete').addEventListener('click', onDeleteFolder);
  }
}

async function openDoc(folderKey, docKey) {
  state.folderView = 'doc';
  try {
    const doc = await apiClient.getDoc(folderKey, docKey);
    state.currentDoc = doc || { key: docKey, title: docKey, body: '', meta: '' };
  } catch (e) {
    captureError(e, { stage: 'openDoc', folderKey, docKey });
    state.currentDoc = { key: docKey, title: docKey, body: '', meta: '' };
  }
  renderLibrary();
  recordNav();
}

function renderDocView() {
  const folder = state.currentFolder || { key: '', title: '' };
  const doc = state.currentDoc || { key: '', title: '', body: '', meta: '' };
  $('docCrumbFolder').textContent = folder.title || folder.key;
  $('docCrumbDoc').textContent = doc.title || doc.key;
  $('docTitleBig').textContent = doc.title || doc.key;
  $('docMetaBig').textContent = formatMetaLine(doc.meta || '');
  $('docBody').innerHTML = renderDocBodyHtml(doc);
  $('docActions').innerHTML = `
    <button class="pill" id="docCopy"><i class="fa-solid fa-copy"></i> Copy</button>
    <button class="pill" id="docDownload"><i class="fa-solid fa-download"></i> Download</button>
    <button class="pill" id="docShare"><i class="fa-solid fa-share-from-square"></i> Share</button>
    <button class="pill danger" id="docDelete"><i class="fa-solid fa-trash"></i> Delete</button>`;
  $('docCopy').addEventListener('click', () => openCopyFormatModal(doc));
  $('docDownload').addEventListener('click', () => onDownloadDoc(doc));
  $('docShare').addEventListener('click', () => openShareModal(folder, doc));
  $('docDelete').addEventListener('click', onDeleteDocFromDetail);
}

// Copy-as-X modal for synthesized docs. The user picks between markdown
// (preserves headings, lists, links) or plain text (strips formatting for
// casual paste into messaging apps).
function openCopyFormatModal(doc) {
  const modal = $('copyFormatModal');
  if (!modal) return;
  const md = $('copyFormatMdBtn');
  const plain = $('copyFormatPlainBtn');
  const cancel = $('copyFormatCancelBtn');
  const close = () => {
    modal.classList.remove('open');
    modal.setAttribute('aria-hidden', 'true');
  };
  const onMd = async () => {
    await navigator.clipboard.writeText(doc.body || '').catch(() => {});
    toast('Copied as Markdown');
    close();
  };
  const onPlain = async () => {
    await navigator.clipboard.writeText(stripMarkdown(doc.body || '')).catch(() => {});
    toast('Copied as plain text');
    close();
  };
  // Replace listeners by cloning the nodes so repeat opens don't stack handlers.
  const fresh = (el) => {
    const c = el.cloneNode(true);
    el.parentNode.replaceChild(c, el);
    return c;
  };
  fresh(md).addEventListener('click', onMd);
  fresh(plain).addEventListener('click', onPlain);
  fresh(cancel).addEventListener('click', close);
  modal.classList.add('open');
  modal.setAttribute('aria-hidden', 'false');
}

// Share modal — frozen-snapshot sharing. The server copies the note's
// content at share time; the public link only changes when the user
// explicitly clicks "Update shared version" here. Sayermore's automatic
// synthesis edits never reach the shared copy.
async function openShareModal(folder, doc) {
  const modal = $('shareModal');
  if (!modal) return;
  if (!apiClient.enabled) {
    toast('Sign in to share notes.');
    return;
  }
  // Replace listeners by cloning the nodes so repeat opens don't stack handlers.
  const fresh = (el) => {
    const c = el.cloneNode(true);
    el.parentNode.replaceChild(c, el);
    return c;
  };
  const close = () => {
    modal.classList.remove('open');
    modal.setAttribute('aria-hidden', 'true');
  };

  let share = null;
  let busy = false;

  const paint = () => {
    const shared = Boolean(share);
    $('shareLinkRow').style.display = shared ? 'flex' : 'none';
    $('shareMeta').style.display = shared ? 'block' : 'none';
    $('shareCreateBtn').style.display = shared ? 'none' : '';
    $('shareRefreshBtn').style.display = shared ? '' : 'none';
    $('shareRevokeBtn').style.display = shared ? '' : 'none';
    if (shared) {
      $('shareLinkInput').value = share.url;
      $('shareMeta').textContent = `Anyone with the link sees the version from ${new Date(share.updatedAt).toLocaleString()}.`;
      $('shareModalDesc').textContent = 'This note is shared as a snapshot. Changes since then — including ones Sayermore makes automatically — stay private until you update the shared version.';
    } else {
      $('shareModalDesc').textContent = 'Sharing publishes a snapshot of this note as it is right now. Later changes — including ones Sayermore makes automatically — stay private until you update the shared version.';
    }
  };

  const snapshot = async (msg) => {
    if (busy) return;
    busy = true;
    try {
      share = await apiClient.createShare(folder.key, doc.key);
      paint();
      toast(msg);
    } catch (e) {
      captureError(e, { stage: 'createShare' });
      toast('Could not share the note. Try again.');
    } finally {
      busy = false;
    }
  };

  fresh($('shareCloseBtn')).addEventListener('click', close);
  fresh($('shareCreateBtn')).addEventListener('click', () => snapshot('Link created — this exact version is now shareable.'));
  fresh($('shareRefreshBtn')).addEventListener('click', () => snapshot('Shared version updated to the current note.'));
  fresh($('shareCopyBtn')).addEventListener('click', async () => {
    await navigator.clipboard.writeText($('shareLinkInput').value || '').catch(() => {});
    toast('Link copied');
  });
  fresh($('shareRevokeBtn')).addEventListener('click', async () => {
    if (busy || !share) return;
    busy = true;
    try {
      await apiClient.revokeShare(share.id);
      share = null;
      paint();
      toast('Sharing stopped. The old link is dead for good — sharing again makes a new one.');
    } catch (e) {
      captureError(e, { stage: 'revokeShare' });
      toast('Could not stop sharing. Try again.');
    } finally {
      busy = false;
    }
  });

  paint();
  modal.classList.add('open');
  modal.setAttribute('aria-hidden', 'false');

  // Load current share state after opening so the modal feels instant.
  const shares = await apiClient.listShares();
  share = shares.find((s) => s.folderKey === folder.key && s.docKey === doc.key) || null;
  paint();
}

// Public share viewer (/s/<token>). Renders the frozen snapshot with the
// same renderMarkdown → DOMPurify path the private doc view uses — do not
// add a second sanitize path.
async function initShareView(id) {
  document.querySelector('.app-shell')?.setAttribute('hidden', '');
  const view = $('shareView');
  if (view) view.hidden = false;
  try {
    const share = await apiClient.fetchPublicShare(id);
    document.title = `${share.title} — Sayermore`;
    $('shareViewTitle').textContent = share.title;
    $('shareViewMeta').textContent = `Shared ${new Date(share.updatedAt).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}`;
    $('shareViewBody').innerHTML = renderMarkdown(share.body || '');
  } catch (e) {
    document.title = 'Sayermore';
    $('shareViewTitle').textContent = 'This note is no longer shared.';
    $('shareViewMeta').textContent = 'The link may have been turned off by its owner.';
    $('shareViewBody').innerHTML = '';
  }
}

// Drop the common markdown syntax characters for a "plain text" copy.
// Best-effort — handles headings, emphasis, links, blockquotes, list bullets,
// inline code, and code fences. Leaves the prose itself intact.
function stripMarkdown(src) {
  let out = String(src || '');
  // Fenced code blocks: keep the inner text, drop the fences + language tag.
  out = out.replace(/```[^\n]*\n([\s\S]*?)```/g, (_, inner) => inner);
  // Inline code: `foo` -> foo
  out = out.replace(/`([^`]+)`/g, '$1');
  // Images: ![alt](url) -> alt
  out = out.replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1');
  // Links: [text](url) -> text
  out = out.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1');
  // Headings: leading # / ## / etc.
  out = out.replace(/^#{1,6}\s+/gm, '');
  // Blockquotes
  out = out.replace(/^>\s?/gm, '');
  // Unordered list bullets
  out = out.replace(/^[\s]*[-*+]\s+/gm, '');
  // Ordered list numbers
  out = out.replace(/^[\s]*\d+\.\s+/gm, '');
  // Bold / italic markers (greedy but bounded)
  out = out.replace(/\*\*([^*]+)\*\*/g, '$1');
  out = out.replace(/__([^_]+)__/g, '$1');
  out = out.replace(/\*([^*\n]+)\*/g, '$1');
  out = out.replace(/(?<!\w)_([^_\n]+)_(?!\w)/g, '$1');
  // Horizontal rules
  out = out.replace(/^[-*_]{3,}\s*$/gm, '');
  // Collapse multiple blank lines
  out = out.replace(/\n{3,}/g, '\n\n');
  return out.trim();
}

// Cross-OS safe filename — strips characters illegal on Windows, collapses
// whitespace, and caps length. Used by single-doc download + folder export.
function sanitizeFilename(name) {
  const trimmed = String(name || '').trim();
  const safe = trimmed.replace(/[\\/:*?"<>|]/g, '-').replace(/\s+/g, ' ').trim();
  return safe.slice(0, 80) || 'untitled';
}

// Trigger a browser download of a Blob with the given filename. Pattern
// mirrors the (removed) exportCaptures helper from earlier in the file's
// history — temporary anchor + URL.createObjectURL + revoke.
function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Defer revoke so Safari has time to start the download.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function onDownloadDoc(doc) {
  if (!doc || !(doc.body || '').trim()) {
    toast('Nothing to download.');
    return;
  }
  const blob = new Blob([doc.body], { type: 'text/markdown' });
  triggerDownload(blob, `${sanitizeFilename(doc.title || doc.key)}.md`);
  toast('Downloaded');
}

// Bulk export every doc in a folder as a zip of individual .md files.
// Each doc keeps its title as the filename so the user can re-import to
// Obsidian/Notion/etc and keep the per-file structure those tools expect.
async function onExportFolder(folder) {
  if (!folder) return;
  const btn = document.getElementById('folderExport');
  try {
    if (btn) { btn.disabled = true; btn.classList.add('busy'); }
    toast('Exporting folder…');
    const { docs } = await apiClient.listDocsInFolder(folder.key);
    if (!Array.isArray(docs) || docs.length === 0) {
      toast('Folder is empty.');
      return;
    }
    // Parallel body fetch — N round-trips but they share connection pool.
    const fulls = await Promise.all(docs.map((d) => apiClient.getDoc(folder.key, d.key)));
    const zip = new JSZip();
    let written = 0;
    fulls.forEach((d, i) => {
      const meta = docs[i] || {};
      const title = (d && d.title) || meta.title || meta.key || 'untitled';
      const body = (d && d.body) || '';
      if (!body.trim()) return;
      zip.file(`${sanitizeFilename(title)}.md`, body);
      written += 1;
    });
    if (written === 0) {
      toast('Folder has no exportable content.');
      return;
    }
    const blob = await zip.generateAsync({ type: 'blob' });
    triggerDownload(blob, `${sanitizeFilename(folder.title || folder.key)}.zip`);
    toast('Folder exported');
  } catch (e) {
    captureError(e, { stage: 'onExportFolder', key: folder?.key });
    toast('Export failed. Try again.');
  } finally {
    if (btn) { btn.disabled = false; btn.classList.remove('busy'); }
  }
}

function renderDocBodyHtml(doc) {
  const inner = renderDocBodyInner(doc);
  // Wrap in .md-content so the same typographic styles the desktop app
  // uses (DM Serif Display headings, italic blockquotes, etc.) apply here.
  return `<div class="md-content">${inner}</div>`;
}

function renderDocBodyInner(doc) {
  const body = (doc.body || '').trim();
  if (body) return renderMarkdown(body);
  // Fallback: if synthesis returned an empty body, surface the raw transcript
  // from the linked session(s) so the user always sees their content.
  const sessionIds = Array.isArray(doc.sessions) ? doc.sessions : [];
  const transcripts = sessionIds
    .map((sid) => state.captures.find((c) => c.id === sid)?.text)
    .filter((t) => t && t.trim());
  if (transcripts.length) {
    return transcripts.map((t) => `<p>${escapeHtml(t)}</p>`).join('');
  }
  const preview = (doc.preview || '').trim();
  if (preview) return `<p>${escapeHtml(preview)}</p>`;
  return '<p class="empty-inline">No content yet.</p>';
}

function closeDoc(toRoot) {
  state.currentDoc = null;
  if (toRoot) {
    state.folderView = 'root';
    state.currentFolder = null;
    state.currentDocs = [];
  } else {
    state.folderView = 'folder';
  }
  renderLibrary();
  recordNav();
}

function docCard(d) {
  const label = d.title || d.key;
  return `<button class="doc-item" data-key="${escapeAttr(d.key)}">
    <div class="doc-head">
      <span class="doc-icon"><i class="fa-solid fa-file-lines"></i></span>
      <span class="doc-title">${escapeHtml(label)}</span>
      <span class="doc-updated">${escapeHtml(d.updated ? formatDate(d.updated) : '')}</span>
      <span class="doc-delete" data-delete-key="${escapeAttr(d.key)}" role="button" tabindex="-1" title="Delete" aria-label="Delete ${escapeAttr(label)}"><i class="fa-solid fa-trash"></i></span>
    </div>
    <div class="doc-preview">${escapeHtml(d.preview || '')}</div>
  </button>`;
}

function closeFolder() {
  state.folderView = 'root';
  state.currentFolder = null;
  state.currentDocs = [];
  renderLibrary();
  recordNav();
}

function renderSessions() {
  // Hide IDB-persisted captures when the user is signed out (and not in
  // the anonymous-demo flow). Captures stay in IDB — just not rendered —
  // so they reappear when the user signs back in. Matches the Notes
  // sub-tab's "Sign in to see your synced library" pattern.
  const inDemo = !isSignedIn() && (typeof localStorage !== 'undefined') && localStorage.getItem(DEMO_SEEDED_KEY) === '1';
  const signedOut = isAuthConfigured() && !isSignedIn() && !inDemo;
  const emptyEl = $('emptyState');
  const signedOutEl = $('sessionsSignedOut');
  const list = $('captureList');
  if (signedOut && state.captures.some((c) => c.demo !== true)) {
    if (emptyEl) emptyEl.style.display = 'none';
    if (signedOutEl) signedOutEl.style.display = 'block';
    if (list) list.innerHTML = '';
    return;
  }
  if (signedOutEl) signedOutEl.style.display = 'none';
  if (emptyEl) emptyEl.style.display = state.captures.length ? 'none' : 'block';
  if (list) list.innerHTML = state.captures.map(captureCard).join('');
  document.querySelectorAll('.capture-item').forEach((item) => item.addEventListener('click', () => openCapture(item.dataset.id)));
}

// Reflections sub-tab. Lists Claude-generated passive observations over the
// user's most recent docs. State.reflections is lazy-loaded the first time
// the tab is rendered (or after Generate).
async function renderReflectionsView() {
  const empty = document.getElementById('reflectionsEmpty');
  const list = document.getElementById('reflectionList');
  const btn = document.getElementById('generateReflectionBtn');
  if (!list || !empty || !btn) return;
  if (!apiClient.enabled) {
    list.innerHTML = '';
    empty.style.display = 'block';
    empty.innerHTML = '<h3>Sign in to see reflections</h3><p>Reflections need your synced library — only available with an account.</p>';
    btn.disabled = true;
    return;
  }
  btn.disabled = false;
  // Lazy fetch first time. Re-fetch is explicit via Generate.
  if (!Array.isArray(state.reflections)) {
    list.innerHTML = '<div class="reflection-loading"><i class="fa-solid fa-circle-notch fa-spin"></i> Loading…</div>';
    empty.style.display = 'none';
    state.reflections = await apiClient.listReflections();
  }
  if (!state.reflections.length) {
    list.innerHTML = '';
    empty.style.display = 'block';
    return;
  }
  empty.style.display = 'none';
  list.innerHTML = state.reflections.map((r) => reflectionCard(r)).join('');
  list.querySelectorAll('[data-source-folder]').forEach((el) => {
    el.addEventListener('click', async () => {
      state.libTab = 'organized';
      await openFolder(el.dataset.sourceFolder);
      await openDoc(el.dataset.sourceFolder, el.dataset.sourceDoc);
    });
  });
}

function reflectionCard(r) {
  const date = formatDate(r.createdAt);
  const bodyHtml = renderMarkdown(r.body);
  const sources = Array.isArray(r.sourceDocs) ? r.sourceDocs : [];
  const sourcesHtml = sources.length
    ? `<div class="reflection-sources">
        <div class="reflection-sources-label">Drawn from</div>
        ${sources.map((s) => `
          <button class="reflection-source" data-source-folder="${escapeAttr(s.folderKey)}" data-source-doc="${escapeAttr(s.docKey)}">
            <span class="reflection-source-title">${escapeHtml(s.title || s.docKey)}</span>
            <i class="fa-solid fa-arrow-right"></i>
          </button>
        `).join('')}
      </div>`
    : '';
  return `
    <article class="reflection-card">
      <header class="reflection-card-head">
        <i class="fa-solid fa-eye"></i>
        <span>${escapeHtml(date)}</span>
      </header>
      <div class="md-content reflection-body">${bodyHtml}</div>
      ${sourcesHtml}
    </article>`;
}

async function onGenerateReflectionClick() {
  if (!apiClient.enabled) {
    toast('Sign in to generate reflections.');
    return;
  }
  const btn = document.getElementById('generateReflectionBtn');
  if (btn) { btn.disabled = true; btn.classList.add('busy'); }
  toast('Reading your recent library…');
  try {
    const reflection = await apiClient.generateReflection();
    if (!reflection) {
      toast('Generation failed. Try again in a moment.');
      return;
    }
    if (reflection.error === 'not_enough_content') {
      toast('Record a couple more captures first — there isn’t enough yet to notice patterns.');
      return;
    }
    // Prepend the new reflection so it lands at the top.
    state.reflections = [reflection, ...(state.reflections || [])];
    renderReflectionsView();
    toast('New reflection ready');
  } catch (e) {
    captureError(e, { stage: 'onGenerateReflectionClick' });
    toast('Generation failed. Try again in a moment.');
  } finally {
    if (btn) { btn.disabled = false; btn.classList.remove('busy'); }
  }
}

function openCapture(id) {
  const c = state.captures.find((item) => item.id === id);
  if (!c) return;
  state.libTab = 'sessions';
  state.libTabAutoSet = true;
  state.sessionsView = 'detail';
  state.currentCapture = c;
  renderLibrary();
  recordNav();
}

function closeCapture() {
  state.sessionsView = 'list';
  state.currentCapture = null;
  renderLibrary();
  recordNav();
}

function renderCaptureDetail() {
  const c = state.currentCapture;
  if (!c) { closeCapture(); return; }
  photoDetailUrls.forEach((url) => URL.revokeObjectURL(url));
  photoDetailUrls = [];
  $('captureCrumbTitle').textContent = c.title;
  $('captureTitle').textContent = c.title;
  $('captureMeta').innerHTML = `${escapeHtml(labelFor(c.topic))} <span>&middot;</span> ${escapeHtml(formatDate(c.createdAt))} <span>&middot;</span> ${escapeHtml(c.mode)} ${syncBadgeHtml(c)}`;
  // If the badge is in the "failed" state, tapping it kicks off a retry.
  const failedBadge = $('captureMeta').querySelector('.sync-badge.sync-failed');
  if (failedBadge) failedBadge.addEventListener('click', () => retrySynthesis(c));
  // Local IDB audio blob (rare — we drop blobs after Whisper). Cloud-stored
  // audio is fetched on demand below.
  const localAudio = c.audio ? `<div class="audio-block"><audio controls src="${URL.createObjectURL(c.audio)}"></audio></div>` : '';
  const cloudAudioSlot = c.hasAudio && !c.audio ? '<div class="audio-block" id="cloudAudioSlot"><span class="audio-loading">Loading audio…</span></div>' : '';
  $('captureBody').innerHTML = `<div class="detail-text">${escapeHtml(c.text || c.prompt || '')}</div>${localAudio}${cloudAudioSlot}`;
  $('captureActions').innerHTML = `
    <button class="pill" id="copyCapture"><i class="fa-solid fa-copy"></i> Copy</button>
    <button class="pill danger" id="deleteCapture"><i class="fa-solid fa-trash"></i> Delete</button>`;
  if (c.mode === 'photo') renderPhotoDetail(c);
  $('copyCapture').addEventListener('click', () => copyCapture(c));
  $('deleteCapture').addEventListener('click', () => deleteCapture(c.id));
  // Fetch a signed playback URL for cloud-stored audio (paid feature).
  if (c.hasAudio && !c.audio) {
    apiClient.getAudioPlaybackUrl(c.id).then((url) => {
      const slot = $('cloudAudioSlot');
      if (!slot) return; // user navigated away
      if (url) {
        slot.innerHTML = `<audio controls preload="metadata" src="${escapeAttr(url)}"></audio>`;
      } else {
        slot.innerHTML = '<span class="audio-loading">Audio unavailable.</span>';
      }
    }).catch((e) => captureError(e, { stage: 'renderCaptureDetail:getAudio', id: c.id }));
  }
  // Library-changes expander: which docs did this session's synthesis touch?
  // Hidden until we get a non-empty response (or stays hidden if zero).
  renderSessionDocsExpander(c);
}

// Fetch the docs touched by this session and render them as an expander.
// Hidden when zero (typical for not-yet-synthesized captures or sessions
// that synthesized into a single fallback). Each row → opens the doc.
async function renderSessionDocsExpander(capture) {
  const host = $('sessionDocs');
  if (!host) return;
  host.hidden = true;
  host.innerHTML = '';
  if (!apiClient.enabled || !capture.synthesizedAt || capture.synthesizedAt === 'failed') {
    return;
  }
  let docs = [];
  try {
    docs = await apiClient.getSessionDocs(capture.id);
  } catch (e) {
    captureError(e, { stage: 'getSessionDocs', id: capture.id });
    return;
  }
  // If the user navigated away while waiting, bail.
  if (!state.currentCapture || state.currentCapture.id !== capture.id) return;
  if (!docs.length) return;
  host.hidden = false;
  host.innerHTML = `
    <details class="session-docs-expander" open>
      <summary>
        <i class="fa-solid fa-chevron-right session-docs-caret"></i>
        Session captures <span class="session-docs-count">${docs.length}</span>
      </summary>
      <div class="session-docs-list">
        ${docs.map((d) => `
          <button class="session-doc-row" data-folder="${escapeAttr(d.folderKey)}" data-doc="${escapeAttr(d.docKey)}">
            <span class="session-doc-chip session-doc-${d.op}">${d.op === 'created' ? 'Created' : 'Updated'}</span>
            <span class="session-doc-title">${escapeHtml(d.title || d.docKey)}</span>
            <i class="fa-solid fa-arrow-right session-doc-arrow"></i>
          </button>
        `).join('')}
      </div>
    </details>`;
  host.querySelectorAll('.session-doc-row').forEach((btn) => {
    btn.addEventListener('click', async () => {
      // The user is on the Captures sub-tab (libTab='sessions') with no
      // current folder. openDoc alone won't paint — renderLibrary routes to
      // renderDocView only when libTab='organized', and the doc breadcrumb
      // expects state.currentFolder set. Hop the rails before opening:
      state.libTab = 'organized';
      await openFolder(btn.dataset.folder);
      await openDoc(btn.dataset.folder, btn.dataset.doc);
    });
  });
}

function renderStorage() {
  const used = state.captures.reduce((sum, item) => sum + estimateCaptureBytes(item), 0);
  const quota = storageQuotaBytes();
  const pct = Math.min(100, (used / quota) * 100);
  $('storageFill').style.width = `${pct}%`;
  $('storageText').textContent = `${formatBytes(used)} of ${formatBytes(quota)} local`;
  $('planText').textContent = planLabel(state.planStatus);
  const summary = $('storageSummary');
  if (summary) {
    const captureCount = state.captures.length;
    summary.textContent = `${formatBytes(used)} used · ${planLabel(state.planStatus)} · ${captureCount} capture${captureCount === 1 ? '' : 's'}`;
  }
  // Pending-sync pill: count captures that haven't been pushed to the
  // cloud (cloudSynced !== true). Hidden when zero. Tappable to force
  // a sync (bypasses WiFi gate). Tooltip hints at WiFi-only mode if on.
  const pendingCount = state.captures.filter((c) =>
    c.demo !== true
    && c.cloudSynced !== true
    && ((c.text || '').trim() || (c.audio && c.transcribed === false)),
  ).length;
  const pill = $('pendingSyncPill');
  if (pill) {
    if (savingInFlight && apiClient.enabled) {
      // A save is running right now (user may have navigated here mid-save).
      // Non-actionable state: syncPendingCaptures/onSyncNowClick both
      // no-op while savingInFlight, so the pill is purely informational.
      pill.style.display = 'inline-flex';
      pill.classList.add('syncing');
      pill.innerHTML = '<i class="fa-solid fa-circle-notch fa-spin"></i> Finishing capture…';
      pill.title = 'Your capture is still saving.';
    } else if (pendingCount === 0 || !apiClient.enabled) {
      pill.classList.remove('syncing');
      pill.style.display = 'none';
    } else {
      pill.classList.remove('syncing');
      pill.style.display = 'inline-flex';
      pill.innerHTML = `<i class="fa-solid fa-cloud-arrow-up"></i> ${pendingCount} pending`;
      pill.title = state.settings.syncWifiOnly
        ? 'Waiting for WiFi. Tap to sync now on any network.'
        : 'Tap to sync now.';
    }
  }
}

function captureCard(c) {
  const tone = c.topic === 'place' ? 'blue' : c.topic === 'skill' ? 'amber' : c.topic === 'freeform' ? 'green' : 'purple';
  const icon = c.topic === 'place' ? 'fa-location-dot' : c.topic === 'skill' ? 'fa-hammer' : c.topic === 'freeform' ? 'fa-road' : 'fa-user';
  const preview = c.text || (c.audio ? 'Audio capture' : 'Untitled capture');
  return `<button class="capture-item" data-id="${escapeAttr(c.id)}">
    <div class="capture-head">
      <span class="capture-type ${tone}"><i class="fa-solid ${icon}"></i></span>
      <span class="capture-title">${escapeHtml(c.title)} ${captureSyncDotHtml(c)}</span>
      <span class="capture-date">${formatDate(c.createdAt)}</span>
    </div>
    <div class="capture-preview">${escapeHtml(preview)}</div>
  </button>`;
}

// Search overlay does substring-only find across captures. Ask lives on
// its own bottom-nav tab now (see onAskSubmit / renderAskAnswer).
function openSearch() {
  $('searchOverlay').classList.add('open');
  $('searchInput').value = '';
  $('searchResults').innerHTML = '';
  setTimeout(() => $('searchInput').focus(), 40);
}

function closeSearch() {
  $('searchOverlay').classList.remove('open');
}

function runSearch() {
  const query = $('searchInput').value.trim().toLowerCase();
  if (!query) {
    $('searchResults').innerHTML = '';
    return;
  }
  const hits = state.captures.filter((c) => `${c.title} ${c.text} ${c.prompt}`.toLowerCase().includes(query));
  $('searchResults').innerHTML = hits.map((c) => `<div class="result" data-id="${escapeAttr(c.id)}"><strong>${escapeHtml(c.title)}</strong><br><small>${escapeHtml(formatDate(c.createdAt))}</small></div>`).join('');
  document.querySelectorAll('.result').forEach((item) => item.addEventListener('click', () => {
    closeSearch();
    switchTab('library');
    openCapture(item.dataset.id);
  }));
}

// Ask v2 — chat-style UI with sub-tabs (Ask = current Q&A, History = past).
// apiClient.ask wraps fetch in a 75s AbortController matched to the server's
// 45s upstream cap with headroom for Render cold-starts.

async function onAskSubmit(event) {
  if (event) event.preventDefault();
  const input = document.getElementById('askInput');
  const btn = document.getElementById('askSubmitBtn');
  if (!input) return;
  const question = (input.value || '').trim();
  if (!question) return;
  if (!apiClient.enabled) {
    state.currentAsk = { question, error: 'not_signed_in', askedAt: new Date().toISOString() };
    setAskSubTab('ask');
    renderAskCurrent();
    return;
  }
  // Move to Ask sub-tab, render the loading state with the question pinned.
  state.currentAsk = { question, loading: true, askedAt: new Date().toISOString() };
  setAskSubTab('ask');
  renderAskCurrent();
  input.value = '';
  autoGrowAskInput();
  if (btn) { btn.disabled = true; btn.classList.add('busy'); }
  const resp = await apiClient.ask(question);
  if (btn) { btn.disabled = false; btn.classList.remove('busy'); }
  if (!resp || resp.error || !String(resp.answer || '').trim()) {
    state.currentAsk = { question, error: resp?.error || (resp ? 'empty_answer' : 'network'), askedAt: state.currentAsk.askedAt };
    renderAskCurrent();
    return;
  }
  const entry = {
    id: (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`,
    question,
    answer: resp.answer || '',
    citations: Array.isArray(resp.citations) ? resp.citations : [],
    askedAt: state.currentAsk.askedAt,
  };
  state.currentAsk = entry;
  state.askHistory = [entry, ...state.askHistory.filter((h) => h.id !== entry.id)].slice(0, 50);
  saveAskHistory();
  renderAskCurrent();
  renderAskHistory();
}

// Composer auto-grow up to 4 lines. Falls back gracefully on platforms
// where scrollHeight doesn't reflect content accurately.
function autoGrowAskInput() {
  const input = document.getElementById('askInput');
  if (!input) return;
  input.style.height = 'auto';
  const cap = 4 * 22 + 24; // ~4 lines + padding
  input.style.height = `${Math.min(input.scrollHeight, cap)}px`;
}

// Sub-tab routing inside #askPanel — Ask vs History. Pure render change.
function setAskSubTab(sub) {
  if (sub !== 'ask' && sub !== 'history') return;
  state.askSubTab = sub;
  document.querySelectorAll('#askSubtabs button').forEach((b) => b.classList.toggle('active', b.dataset.asksub === sub));
  const cur = document.getElementById('askViewCurrent');
  const hist = document.getElementById('askViewHistory');
  if (cur) cur.classList.toggle('active', sub === 'ask');
  if (hist) hist.classList.toggle('active', sub === 'history');
  if (sub === 'ask') {
    renderAskCurrent();
    scrollAskFeedToBottom();
  } else {
    renderAskHistory();
  }
}

function scrollAskFeedToBottom() {
  const feed = document.getElementById('askFeed');
  if (!feed) return;
  // Defer to next frame so DOM mutations settle before measuring.
  requestAnimationFrame(() => { feed.scrollTop = feed.scrollHeight; });
}

// localStorage-backed Ask history. Per-device by design — Ask is personal,
// sync feels heavy. 50-item cap stays well under localStorage's quota.
const ASK_HISTORY_KEY = 'sayermore.askHistory';

function loadAskHistory() {
  try {
    const raw = localStorage.getItem(ASK_HISTORY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.slice(0, 50) : [];
  } catch {
    return [];
  }
}

function saveAskHistory() {
  try {
    localStorage.setItem(ASK_HISTORY_KEY, JSON.stringify(state.askHistory.slice(0, 50)));
  } catch (e) {
    // Quota / private-mode failures are non-fatal — the history just stays
    // in memory for this session.
    captureError(e, { stage: 'saveAskHistory' });
  }
}

// History sub-tab — chat-style preview cards of past asks. Tap loads that
// ask into the Ask sub-tab as the current Q&A (no network call).
function renderAskHistory() {
  const host = document.getElementById('askHistory');
  if (!host) return;
  const items = state.askHistory || [];
  if (items.length === 0) {
    host.innerHTML = '<div class="ask-history-empty">No past asks yet.</div>';
    return;
  }
  host.innerHTML = items.map((it) => {
    const previewText = (it.answer || '').replace(/[#*`>_]/g, '').slice(0, 160);
    const more = (it.answer || '').length > 160 ? '…' : '';
    const sourceCount = Array.isArray(it.citations) ? it.citations.length : 0;
    return `
      <button class="ask-history-card" data-ask-id="${escapeAttr(it.id)}">
        <div class="ask-history-card-q">
          <i class="fa-solid fa-message"></i>
          <span>${escapeHtml(it.question)}</span>
        </div>
        <div class="ask-history-card-a">${escapeHtml(previewText)}${more}</div>
        <div class="ask-history-card-foot">
          <span>${escapeHtml(relativeTime(it.askedAt))}</span>
          ${sourceCount ? `<span class="ask-history-card-sources"><i class="fa-solid fa-link"></i> ${sourceCount} source${sourceCount === 1 ? '' : 's'}</span>` : ''}
        </div>
      </button>`;
  }).join('');
  host.querySelectorAll('.ask-history-card').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = btn.dataset.askId;
      const item = state.askHistory.find((x) => x.id === id);
      if (!item) return;
      state.currentAsk = item;
      setAskSubTab('ask');
    });
  });
}

async function onClearAskHistoryClick() {
  if (!state.askHistory || state.askHistory.length === 0) return;
  if (!confirm('Clear all past asks? This wipes the list on this device only.')) return;
  state.askHistory = [];
  saveAskHistory();
  renderAskHistory();
}

// Lightweight relative-time formatter for Ask history rows.
function relativeTime(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return '';
  if (ms < 60_000) return 'just now';
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
  return formatDate(iso);
}

// Ask sub-tab — renders state.currentAsk as a chat thread (Q bubble + A
// bubble + sources). Empty state shows when state.currentAsk is null.
function renderAskCurrent() {
  const empty = document.getElementById('askEmpty');
  const thread = document.getElementById('askThread');
  if (!thread || !empty) return;
  const cur = state.currentAsk;
  if (!cur) {
    empty.style.display = 'flex';
    thread.innerHTML = '';
    return;
  }
  empty.style.display = 'none';

  // Question bubble is always shown.
  let html = `
    <div class="ask-bubble ask-bubble-q">
      <div class="ask-bubble-body">${escapeHtml(cur.question)}</div>
    </div>`;

  if (cur.loading) {
    html += `
      <div class="ask-bubble ask-bubble-a ask-bubble-loading">
        <div class="ask-bubble-body">
          <i class="fa-solid fa-circle-notch fa-spin"></i>
          <span>Reading your library…</span>
          <span class="ask-loading-sub">Usually 3–8 seconds.</span>
        </div>
      </div>`;
  } else if (cur.error) {
    const code = cur.error;
    const msg = code === 'empty_answer'
      ? 'No answer came back. Please try again — your saved memories are safe.'
      : code === 'not_signed_in'
      ? 'Sign in to ask your library.'
      : code === 'client_timeout' || code === 'upstream_timeout'
        ? "That took too long. Try a shorter question, or try again in a moment — the server may be waking up."
        : code === 'network'
          ? "Couldn't reach the server. Check your connection and try again."
          : 'Ask failed. Try again in a moment.';
    html += `
      <div class="ask-bubble ask-bubble-a ask-bubble-error">
        <div class="ask-bubble-body"><i class="fa-solid fa-triangle-exclamation"></i> ${escapeHtml(msg)}</div>
      </div>`;
  } else {
    const answerHtml = renderMarkdown(cur.answer);
    const citations = Array.isArray(cur.citations) ? cur.citations : [];
    html += `
      <div class="ask-bubble ask-bubble-a">
        <div class="ask-bubble-body md-content">${answerHtml}</div>
        ${citations.length ? `
          <div class="ask-citations">
            <div class="ask-citations-label">Sources</div>
            ${citations.map((c, i) => `
              <button class="ask-citation" data-session="${escapeAttr(c.sessionId || '')}" data-folder="${escapeAttr(c.folderKey || '')}" data-doc="${escapeAttr(c.docKey || '')}">
                <span class="ask-citation-num">${i + 1}</span>
                <span class="ask-citation-title">${escapeHtml(c.title || c.docKey)}</span>
                <i class="fa-solid fa-arrow-right"></i>
              </button>
            `).join('')}
          </div>
        ` : ''}
      </div>`;
  }

  thread.innerHTML = html;
  thread.querySelectorAll('.ask-citation').forEach((btn) => {
    btn.addEventListener('click', async () => {
      switchTab('library');
      if (btn.dataset.session) {
        if (!state.captures.some((c) => c.id === btn.dataset.session)) {
          try {
            const source = await apiClient.getSession(btn.dataset.session);
            if (source) {
              await putCapture({ id: source.id, mode: source.mode, topic: source.topic || 'freeform',
                title: makeTitle(source.transcript, source.topic), text: source.transcript,
                createdAt: source.createdAt, cloudSynced: true, hasImages: source.hasImages, hasAudio: source.hasAudio });
              await loadCaptures();
            }
          } catch { toast('This source could not be loaded. Try again.'); return; }
        }
        if (!state.captures.some((c) => c.id === btn.dataset.session)) { toast('This source is no longer available.'); return; }
        openCapture(btn.dataset.session);
        return;
      }
      state.libTab = 'organized';
      await openFolder(btn.dataset.folder);
      await openDoc(btn.dataset.folder, btn.dataset.doc);
    });
  });
  scrollAskFeedToBottom();
}

function openSettings() {
  $('displayName').value = state.settings.displayName || '';
  $('browserSpeech').checked = Boolean(state.settings.browserSpeech);
  // Default to true if undefined (covers users who saved settings before
  // confirmDiscard existed).
  $('confirmDiscardToggle').checked = state.settings.confirmDiscard !== false;
  $('speakPromptsToggle').checked = Boolean(state.settings.speakPrompts);
  $('syncWifiOnlyToggle').checked = Boolean(state.settings.syncWifiOnly);
  $('settingsDrawer').classList.add('open');
}

function closeSettings() {
  $('settingsDrawer').classList.remove('open');
}

function saveSettings() {
  state.settings = {
    displayName: $('displayName').value.trim(),
    browserSpeech: $('browserSpeech').checked,
    confirmDiscard: $('confirmDiscardToggle').checked,
    speakPrompts: $('speakPromptsToggle').checked,
    syncWifiOnly: $('syncWifiOnlyToggle').checked,
    // Once the user has visited Settings, treat the WiFi prompt as answered.
    wifiPromptAnswered: true,
  };
  localStorage.setItem('sayermore.settings', JSON.stringify(state.settings));
  applySettings();
  closeSettings();
  toast('Settings saved');
}

function applySettings() {
  $('syncLabel').textContent = '';
  $('browserSpeech').checked = Boolean(state.settings.browserSpeech);
  const ct = $('confirmDiscardToggle');
  if (ct) ct.checked = state.settings.confirmDiscard !== false;
  const sp = $('speakPromptsToggle');
  if (sp) sp.checked = Boolean(state.settings.speakPrompts);
  const wf = $('syncWifiOnlyToggle');
  if (wf) wf.checked = Boolean(state.settings.syncWifiOnly);
  // Reflect the in-session speaker icon state too.
  const icon = $('speakerToggleBtn');
  if (icon) icon.classList.toggle('active', Boolean(state.settings.speakPrompts));
  renderTrialBanner();
  renderStorage();
}

// Hardcoded lifetime allowlist — the founder + any future named permanent
// users. Skips the client-side trial timer entirely. Real server-side
// enforcement is the right tool when paying users actually exist; until
// then this 5-line guard is enough.
const LIFETIME_EMAILS = new Set(['thedayisntgray@gmail.com']);

// Who sees the Admin tab. Must mirror the API's ADMIN_EMAILS — the server
// is the real gate; this only controls tab visibility.
const ADMIN_EMAILS = new Set(['thedayisntgray@gmail.com']);

function isAdminUser() {
  const email = getUser()?.primaryEmailAddress?.emailAddress?.toLowerCase();
  return Boolean(email && ADMIN_EMAILS.has(email));
}

// Local (in-browser) storage quota. Founder gets 1 GB; everyone else the
// free 200 MB default. Keyed on the lifetime allowlist (the founder), so
// it tracks whoever that set contains.
function storageQuotaBytes() {
  const email = getUser()?.primaryEmailAddress?.emailAddress?.toLowerCase();
  return email && LIFETIME_EMAILS.has(email) ? 1024 * 1024 * 1024 : FREE_STORAGE_BYTES;
}

function storageUsedBytes() {
  return state.captures.reduce((sum, item) => sum + estimateCaptureBytes(item), 0);
}

// "Full" = at/over quota with what's already on disk. Checked at the START
// of a capture (not at save) so we never drop work a user already made.
function isStorageFull() {
  return storageUsedBytes() >= storageQuotaBytes();
}

// Prompt shown when someone tries to begin a new capture with a full local
// library. Their saved captures are untouched — the block is only on
// starting more until they free up space.
function showStorageLimitModal() {
  const modal = $('storageLimitModal');
  if (!modal) return;
  const desc = $('storageLimitDesc');
  if (desc) {
    desc.textContent = `You're using ${formatBytes(storageUsedBytes())} of ${formatBytes(storageQuotaBytes())} on this device, so there's no room to start a new capture. Everything you've already saved is safe. Export and remove older captures to free up space and resume.`;
  }
  const fresh = (el) => { const c = el.cloneNode(true); el.parentNode.replaceChild(c, el); return c; };
  const close = () => { modal.classList.remove('open'); modal.setAttribute('aria-hidden', 'true'); };
  fresh($('storageLimitCloseBtn')).addEventListener('click', close);
  fresh($('storageLimitManageBtn')).addEventListener('click', () => { close(); switchTab('library'); });
  modal.classList.add('open');
  modal.setAttribute('aria-hidden', 'false');
}

// The server refused this account outright — either the beta is invite-only
// (403 waitlist) or it's full (403 at_capacity). Unlike other failures this
// won't resolve on retry, so it takes over the UI once instead of letting
// every in-flight request raise its own toast. Deliberately not dismissible:
// every cloud call will fail until they're admitted. Locally saved captures
// stay on the device and remain exportable.
let accessDeniedShown = false;
function showAccessModal({ code, detail } = {}) {
  if (accessDeniedShown) return;
  accessDeniedShown = true;
  const modal = $('accessModal');
  if (!modal) return;
  const inviteOnly = code === 'waitlist';
  const title = $('accessTitle');
  const desc = $('accessDesc');
  if (title) title.textContent = inviteOnly ? 'Sayermore is invite-only' : 'Sayermore is at capacity';
  if (desc) {
    desc.textContent = detail || (inviteOnly
      ? "We're still in a closed beta. Join the waitlist and we'll email you when a seat opens."
      : "This beta round is full. Join the waitlist and we'll email you the moment a seat opens.");
  }
  const status = $('accessStatus');
  if (status) { status.style.display = 'none'; status.textContent = ''; }

  const fresh = (el) => { const c = el.cloneNode(true); el.parentNode.replaceChild(c, el); return c; };
  fresh($('accessSignOutBtn')).addEventListener('click', () => signOut());

  const joinBtn = fresh($('accessWaitlistBtn'));
  joinBtn.addEventListener('click', async () => {
    const email = getUser()?.primaryEmailAddress?.emailAddress;
    if (!status) return;
    status.style.display = '';
    if (!email) {
      status.textContent = 'Could not read your email — reach us at hello@sayermore.com.';
      status.className = 'waitlist-status error';
      return;
    }
    joinBtn.disabled = true;
    status.textContent = 'Adding you to the list…';
    status.className = 'waitlist-status pending';
    try {
      // /v1/waitlist is anonymous, so it still works for a blocked account.
      const res = await fetch(`${config.apiUrl}/v1/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source: inviteOnly ? 'app-invite-only' : 'app-at-capacity' }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      status.textContent = data.alreadyOnList
        ? "You're already on the list — we'll be in touch."
        : "You're on the list. We'll email you when a seat opens.";
      status.className = 'waitlist-status success';
    } catch (err) {
      captureError(err, { stage: 'accessWaitlistJoin' });
      joinBtn.disabled = false;
      status.textContent = 'Could not reach the server. Try again, or email hello@sayermore.com.';
      status.className = 'waitlist-status error';
    }
  });

  modal.classList.add('open');
  modal.setAttribute('aria-hidden', 'false');
}

window.addEventListener('sayermore:access-denied', (e) => showAccessModal(e.detail || {}));

async function loadAdminOverview() {
  const status = $('adminStatus');
  const content = $('adminContent');
  if (!content) return;
  if (status) status.textContent = 'Loading…';
  content.replaceChildren();
  try {
    const data = await apiClient.adminOverview();
    if (!data) throw new Error('no data');
    renderAdminOverview(data);
    if (status) status.textContent = 'Provider costs only — excludes Render/Cloudflare fixed spend.';
  } catch (e) {
    captureError(e, { stage: 'adminOverview' });
    if (status) status.textContent = 'Could not load admin data.';
  }
}

function money(cents) {
  return `$${(Number(cents || 0) / 100).toFixed(2)}`;
}

function shortDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

// All admin DOM is built with createElement/textContent — emails and plan
// strings come from the DB and must never hit innerHTML.
function renderAdminOverview(data) {
  const content = $('adminContent');
  if (!content) return;
  const totals = data.totals || {};
  const el = (tag, className, text) => {
    const n = document.createElement(tag);
    if (className) n.className = className;
    if (text != null) n.textContent = text;
    return n;
  };

  const stats = el('div', 'admin-stats');
  [
    [String(totals.userCount ?? 0), 'Users'],
    [String(totals.signups7d ?? 0), 'Signups · 7d'],
    [String(totals.signups30d ?? 0), 'Signups · 30d'],
    [money(totals.costCents30d), 'Cost · 30d'],
    [money(totals.costCentsTotal), 'Cost · all time'],
    [String(totals.waitlistCount ?? 0), 'Waitlist'],
  ].forEach(([big, label]) => {
    const card = el('div', 'admin-stat');
    card.appendChild(el('b', null, big));
    card.appendChild(el('span', null, label));
    stats.appendChild(card);
  });
  content.appendChild(stats);

  const table = (headers, rows) => {
    const wrap = el('div', 'admin-table-wrap');
    const t = el('table', 'admin-table');
    const thead = el('thead');
    const hr = el('tr');
    headers.forEach((h) => hr.appendChild(el('th', null, h)));
    thead.appendChild(hr);
    t.appendChild(thead);
    const tbody = el('tbody');
    rows.forEach((cells) => {
      const tr = el('tr');
      cells.forEach((c) => tr.appendChild(el('td', null, c)));
      tbody.appendChild(tr);
    });
    t.appendChild(tbody);
    wrap.appendChild(t);
    return wrap;
  };

  content.appendChild(el('h3', 'admin-section-title', `Users (${(data.users || []).length})`));
  content.appendChild(table(
    ['Email', 'Plan', 'Signed up', 'Cost 30d', 'Cost total', 'Transcribes (mo)', 'Sessions', 'Storage', 'Last active'],
    (data.users || []).map((u) => [
      u.email || '—',
      u.plan || '—',
      shortDate(u.createdAt),
      money(u.costCents30d),
      money(u.costCentsTotal),
      String(u.transcribesThisMonth ?? 0),
      String(u.sessionCount ?? 0),
      formatBytes(Number(u.storageBytes || 0)),
      shortDate(u.lastActivity),
    ]),
  ));

  content.appendChild(el('h3', 'admin-section-title', 'Cost by endpoint · last 30 days'));
  content.appendChild(table(
    ['Endpoint', 'Model', 'Calls', 'Cost'],
    (data.byEndpoint || []).map((r) => [
      r.endpoint || '—',
      r.model || '—',
      String(r.calls ?? 0),
      money(r.costCents),
    ]),
  ));
}

function applyPlanState() {
  const email = getUser()?.primaryEmailAddress?.emailAddress?.toLowerCase();
  if (email && LIFETIME_EMAILS.has(email)) {
    state.planStatus = 'paid_active';
    state.capabilities = { canCaptureWeb: true, canKeepHostedData: true, canExport: true };
    return;
  }
  // Freemium: free users are never timed out — the server's monthly caps
  // (10 transcriptions, 3 photo captures) do the limiting instead. The old
  // trial countdown (sayermore.trialStartedAt) is intentionally ignored so
  // previously "expired" users come back to a working app.
  state.planStatus = 'free';
  state.capabilities = {
    canCaptureWeb: true,
    canKeepHostedData: true,
    canExport: true,
  };
}

function renderTrialBanner() {
  const banner = $('trialBanner');
  if (!banner) return;
  if (state.planStatus !== 'trial_expired') {
    banner.style.display = 'none';
    return;
  }
  banner.style.display = 'block';
  banner.textContent = 'Your trial has ended. Export your captures anytime, or subscribe to keep using Sayermore Cloud.';
}

function planLabel(status) {
  return {
    trial_active: 'Trial active',
    trial_expired: 'Trial ended',
    free: 'Free plan',
    paid_active: 'Paid active',
    paid_past_due: 'Payment issue',
    anonymous: 'Sign in required',
  }[status] || 'Trial active';
}

function loadSettings() {
  try { return JSON.parse(localStorage.getItem('sayermore.settings') || '{}'); } catch { return {}; }
}


async function copyCapture(c) {
  await navigator.clipboard.writeText(`# ${c.title}\n\n${c.text || c.prompt || ''}`).catch(() => {});
  toast('Copied');
}

// Unstick a capture whose synthesis failed too many times. Clears
// synthesizedAt + syncAttempts so the next sync pass treats it as fresh,
// then kicks a forced sync (bypasses any WiFi-only / cellular gates).
async function retrySynthesis(c) {
  if (!apiClient.enabled) {
    toast('Sign in to sync.');
    return;
  }
  c.synthesizedAt = undefined;
  c.syncAttempts = 0;
  await putCapture(c);
  toast('Retrying synthesis…');
  await syncPendingCaptures({ force: true });
  // Refresh the local state from IDB so the badge reflects the new status.
  await loadCaptures();
  const fresh = state.captures.find((x) => x.id === c.id);
  if (fresh) {
    state.currentCapture = fresh;
    renderCaptureDetail();
  }
  renderLibrary();
}

async function deleteCapture(id) {
  if (photoSyncJobs.has(id)) { toast('This memory is still syncing. Try deleting it once sync finishes.'); return; }
  if (!confirm('Delete this capture? It moves to trash for 30 days, then permanently deletes.')) return;
  await deleteFromDb(id);
  await loadCaptures();
  closeCapture();
  toast('Moved to trash');
  if (apiClient.enabled) {
    apiClient.deleteSession(id).catch((e) => captureError(e, { stage: 'deleteSession', id }));
  }
}

async function deleteDocCascade(folderKey, docKey, doc) {
  let fullDoc = doc;
  if (!fullDoc || !Array.isArray(fullDoc.sessions)) {
    try {
      fullDoc = await apiClient.getDoc(folderKey, docKey);
    } catch (e) {
      captureError(e, { stage: 'deleteDocCascade:getDoc', folderKey, docKey });
    }
  }
  try {
    await apiClient.deleteDoc(folderKey, docKey);
  } catch (e) {
    captureError(e, { stage: 'deleteDocCascade:deleteDoc', folderKey, docKey });
    toast('Failed to delete from cloud.');
    return false;
  }
  const sessionIds = Array.isArray(fullDoc?.sessions) ? fullDoc.sessions : [];
  for (const id of sessionIds) {
    try { await deleteFromDb(id); }
    catch (e) { captureError(e, { stage: 'deleteDocCascade:deleteFromDb', id }); }
    apiClient.deleteSession(id).catch((e) => captureError(e, { stage: 'deleteDocCascade:deleteSession', id }));
  }
  if (sessionIds.length) await loadCaptures();
  state.currentDocs = state.currentDocs.filter((d) => d.key !== docKey);
  return true;
}

async function onDeleteDocFromDetail() {
  const doc = state.currentDoc;
  const folder = state.currentFolder;
  if (!doc || !folder) return;
  const label = doc.title || doc.key;
  if (!confirm(`Delete "${label}"? It moves to trash for 30 days. The linked capture goes with it.`)) return;
  const ok = await deleteDocCascade(folder.key, doc.key, doc);
  if (!ok) return;
  state.currentDoc = null;
  state.folderView = 'folder';
  renderLibrary();
  recordNav();
  toast('Moved to trash');
}

async function onDeleteDocFromList(folderKey, docKey) {
  const stub = state.currentDocs.find((d) => d.key === docKey);
  const label = stub?.title || docKey;
  if (!confirm(`Delete "${label}"? It moves to trash for 30 days. The linked capture goes with it.`)) return;
  const ok = await deleteDocCascade(folderKey, docKey, null);
  if (!ok) return;
  renderLibrary();
  toast('Moved to trash');
}

async function onDeleteFolder() {
  const folder = state.currentFolder;
  if (!folder) return;
  const docs = [...state.currentDocs];
  const n = docs.length;
  const docLabel = `${n} doc${n === 1 ? '' : 's'}`;
  const msg = n
    ? `Delete folder "${folder.title || folder.key}" and ${docLabel} (plus linked captures)? Everything moves to trash for 30 days.`
    : `Delete empty folder "${folder.title || folder.key}"? It moves to trash for 30 days.`;
  if (!confirm(msg)) return;
  for (const d of docs) {
    await deleteDocCascade(folder.key, d.key, null);
  }
  try {
    await apiClient.deleteFolder(folder.key);
  } catch (e) {
    captureError(e, { stage: 'deleteFolder', key: folder.key });
    toast('Failed to delete folder.');
    return;
  }
  state.folderView = 'root';
  state.currentFolder = null;
  state.currentDocs = [];
  await loadFolders();
  renderLibrary();
  recordNav();
  toast('Folder deleted');
}

function buildWave() {
  $('wave').innerHTML = Array.from({ length: 24 }, () => '<span class="bar"></span>').join('');
}

function setStatus(text, cls = '') {
  $('statusText').textContent = text;
  $('statusText').className = `status ${cls}`.trim();
}

// Step progress indicator for the save pipeline, shown on the session
// screen under the Save/Discard buttons. Each step gets a spinner, a
// label, and a duration hint; the long "organizing" step rotates through
// whimsical sub-messages so a 30-60s Claude call doesn't feel dead. A
// persistent warning row tells the user to keep the screen open for the
// whole save (persist-first means leaving isn't fatal, but staying is
// the happy path — especially on mobile where backgrounding suspends
// the in-flight requests).
const SAVE_STEPS = {
  saving: { label: 'Saving…', sub: 'Just a moment.' },
  transcribing: { label: 'Transcribing…', sub: 'Usually 5–20 seconds.' },
  organizing: { label: 'Organizing your capture…', sub: 'Filing this with related memories…' },
};
const ORGANIZE_WHIMSY = [
  'Filing this with related memories…',
  'Finding the right folder…',
  'Usually 20–60 seconds.',
  'Nearly there…',
];
let saveStepRotator = null;
function setSaveStep(step) {
  clearInterval(saveStepRotator);
  saveStepRotator = null;
  const el = $('saveProgress');
  if (!el) return;
  if (!step) {
    el.hidden = true;
    return;
  }
  const def = SAVE_STEPS[step];
  if (!def) return;
  el.hidden = false;
  $('saveProgressLabel').textContent = def.label;
  $('saveProgressSub').textContent = def.sub;
  if (step === 'organizing') {
    let i = 0;
    saveStepRotator = setInterval(() => {
      i = (i + 1) % ORGANIZE_WHIMSY.length;
      const sub = $('saveProgressSub');
      if (sub) sub.textContent = ORGANIZE_WHIMSY[i];
    }, 7000);
  }
}

function makeTitle(text, topic) {
  const clean = text.replace(/\s+/g, ' ').trim();
  if (clean) return clean.split(' ').slice(0, 8).join(' ').replace(/[.,;:!?]$/, '');
  return `${labelFor(topic)} capture`;
}

function slugify(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, 80) || 'untitled';
}

function uniqueDocKey(title, id) {
  return `${slugify(title)}-${String(id || '').slice(0, 8).toLowerCase()}`;
}

function audioExtFromType(type) {
  const mime = String(type || '').toLowerCase();
  if (mime.includes('mp4') || mime.includes('m4a')) return 'mp4';
  if (mime.includes('ogg')) return 'ogg';
  if (mime.includes('wav')) return 'wav';
  if (mime.includes('mpeg') || mime.includes('mp3')) return 'mp3';
  return 'webm';
}

function labelFor(topic) {
  return { person: 'Person', place: 'Place', skill: 'Skill', freeform: 'Free-form' }[topic] || 'Capture';
}

// Resolve the user's preferred name for synthesis prompts. Order:
//   1. Settings displayName (user-entered, ground truth)
//   2. Clerk first name (if signed in and they filled it on signup)
//   3. '' — server will skip the name hint block entirely
// Cap at 80 chars to avoid prompt abuse.
function currentDisplayName() {
  const fromSettings = (state.settings.displayName || '').trim();
  if (fromSettings) return fromSettings.slice(0, 80);
  const u = getUser();
  const fromClerk = (u?.firstName || '').trim();
  return fromClerk.slice(0, 80);
}

// Per-capture sync/synthesis state. Used to render the badge in the detail
// header and the small status dot on each capture card. Order matters:
// demo > local-only (not signed in) > pending push > failed synth >
// synthesizing > synced.
function syncStatusOf(c) {
  if (c.mode === 'photo' && c.backgroundJobId) return { key: c.processingStatus === 'completed' ? 'synced' : ['failed','expired','canceled'].includes(c.processingStatus) ? 'pending' : 'synthesizing', label: photoJobLabel(c), icon: c.processingStatus === 'completed' ? 'fa-circle-check' : 'fa-cloud-arrow-up' };
  if (c.mode === 'photo' && c.photoSyncError) return { key: 'pending', label: c.cloudSynced ? 'Saved · organization pending' : 'Saved on this device · sync pending', icon: 'fa-cloud-arrow-up' };
  if (c.demo === true) return { key: 'demo', label: 'Sample', icon: 'fa-flask' };
  if (!apiClient.enabled) return { key: 'local', label: 'Local only', icon: 'fa-mobile-screen' };
  if (c.transcribed === false) return { key: 'pending', label: 'Waiting to transcribe', icon: 'fa-cloud-arrow-up' };
  if (c.cloudSynced !== true) return { key: 'pending', label: 'Pending sync', icon: 'fa-cloud-arrow-up' };
  if (c.synthesizedAt === 'failed') return { key: 'failed', label: 'Synthesis failed — tap to retry', icon: 'fa-triangle-exclamation' };
  if (!c.synthesizedAt) return { key: 'synthesizing', label: 'Synthesizing…', icon: 'fa-wand-magic-sparkles' };
  return { key: 'synced', label: 'Synced', icon: 'fa-circle-check' };
}

function syncBadgeHtml(c) {
  const s = syncStatusOf(c);
  const clickable = s.key === 'failed' ? ' role="button" tabindex="0"' : '';
  return `<span class="sync-badge sync-${s.key}"${clickable}><i class="fa-solid ${s.icon}"></i> ${escapeHtml(s.label)}</span>`;
}

function captureSyncDotHtml(c) {
  const s = syncStatusOf(c);
  if (s.key === 'synced' || s.key === 'demo') return '';
  return `<i class="fa-solid ${s.icon} sync-dot sync-dot-${s.key}" title="${escapeAttr(s.label)}" aria-label="${escapeAttr(s.label)}"></i>`;
}

function estimateCaptureBytes(capture) {
  const textBytes = new Blob([JSON.stringify({
    id: capture.id,
    mode: capture.mode,
    topic: capture.topic,
    prompt: capture.prompt,
    title: capture.title,
    text: capture.text,
    createdAt: capture.createdAt,
  })]).size;
  return textBytes + (capture.audio ? capture.audio.size : 0) + (capture.voiceAudio?.size || 0) + (capture.images || []).reduce((sum, image) => sum + image.size, 0);
}

function formatBytes(bytes) {
  if (bytes >= 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  if (bytes >= 1024 * 1024) return `${Math.max(0.1, bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.ceil(bytes / 1024)} KB`;
}

function formatDate(iso) {
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(new Date(iso));
}

// Render a "meta" line on a doc. If the meta string parses as an ISO
// date, format it human-friendly; otherwise show it verbatim (synthesizer
// often returns a one-line context note).
function formatMetaLine(meta) {
  const str = String(meta || '').trim();
  if (!str) return '';
  const d = new Date(str);
  if (!isNaN(d.getTime()) && /^\d{4}-\d{2}-\d{2}/.test(str)) {
    return new Intl.DateTimeFormat(undefined, { month: 'long', day: 'numeric', year: 'numeric' }).format(d);
  }
  return str;
}

function toast(message) {
  $('toast').textContent = message;
  $('toast').classList.add('show');
  clearTimeout(toast.id);
  toast.id = setTimeout(() => $('toast').classList.remove('show'), 2400);
}

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function escapeHtml(value) {
  return String(value || '').replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]));
}

function escapeAttr(value) {
  return escapeHtml(value).replace(/`/g, '&#96;');
}

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (event) => {
      const db = req.result;
      const oldV = event.oldVersion;
      if (oldV < 1) {
        if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'id' });
      }
      // Future migrations: branch on oldV here (e.g. if (oldV < 2) addIndex(...))
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    req.onblocked = () => reject(new Error('IDB upgrade blocked by another tab'));
  });
}

function tx(mode = 'readonly') {
  return state.db.transaction(STORE, mode).objectStore(STORE);
}

function putCapture(capture) {
  return new Promise((resolve, reject) => {
    let req;
    try {
      req = tx('readwrite').put(capture);
    } catch (e) {
      captureError(e, { stage: 'putCapture' });
      toast('Could not save capture.');
      reject(e);
      return;
    }
    req.onsuccess = resolve;
    req.onerror = () => {
      const err = req.error;
      if (err && err.name === 'QuotaExceededError') {
        toast('Local storage limit reached. Export or delete captures to make room.');
      } else {
        captureError(err, { stage: 'putCapture' });
      }
      reject(err);
    };
  });
}

function deleteFromDb(id) {
  return new Promise((resolve, reject) => {
    const req = tx('readwrite').delete(id);
    req.onsuccess = resolve;
    req.onerror = () => reject(req.error);
  });
}

function loadCaptures() {
  return new Promise((resolve, reject) => {
    const req = tx().getAll();
    req.onsuccess = () => {
      state.captures = req.result.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
      resolve();
    };
    req.onerror = () => reject(req.error);
  });
}

async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return;
  try {
    const reg = await navigator.serviceWorker.register('sw.js');
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data && event.data.type === 'SW_UPDATED') {
        swUpdatePending = true;
        maybeApplySwUpdate(); // deferred while saving/recording; re-run from save finally
      }
    });
    reg.addEventListener('updatefound', () => {
      const sw = reg.installing;
      if (!sw) return;
      sw.addEventListener('statechange', () => {
        if (sw.state === 'installed' && navigator.serviceWorker.controller) {
          sw.postMessage({ type: 'SKIP_WAITING' });
        }
      });
    });
    setInterval(() => { reg.update().catch(() => {}); }, 60 * 60 * 1000);
  } catch (e) {
    captureError(e, { stage: 'registerServiceWorker' });
  }
}

let pollingPhotoJobs = false;
async function refreshPhotoJobs() {
  if (pollingPhotoJobs || savingInFlight || document.hidden || !apiClient.enabled || !isOnline()) return;
  const pending = state.captures.filter(photoJobPending);
  if (!pending.length) return;
  pollingPhotoJobs = true;
  try {
    const before = JSON.stringify(pending.map(c => [c.processingStatus,c.processingStage,c.text,c.photoSyncError,c.hasImages]));
    for (const c of pending) await processPhotoMemory(c);
    if (before === JSON.stringify(pending.map(c => [c.processingStatus,c.processingStage,c.text,c.photoSyncError,c.hasImages]))) return;
    await loadCaptures();
    if (pending.some(c => c.processingStatus === 'completed')) await loadFolders();
    renderLibrary();
    if (state.currentCapture?.mode === 'photo') {
      state.currentCapture = state.captures.find(c => c.id === state.currentCapture.id) || state.currentCapture;
      renderCaptureDetail();
    }
  } finally { pollingPhotoJobs = false; }
}
setInterval(() => refreshPhotoJobs().catch(() => {}), 4000);
document.addEventListener('visibilitychange', () => { if (!document.hidden) refreshPhotoJobs().catch(() => {}); });
